import { useEffect, useState } from "react";
import { pitchLabel } from "@/lib/pitchLabel";
import api, { formatApiError } from "@/lib/api";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { format, parseISO } from "date-fns";
import { Users, Calendar as CalendarIcon, TrendingUp, MapPin, Plus, Pencil, Trash2, RotateCcw, Upload, Building2, Circle, Bell, BellOff, Printer, ShieldAlert, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import UploadPanel from "@/components/app/UploadPanel";
import DocumentPanel from "@/components/app/DocumentPanel";
import AdminFAImport from "@/pages/AdminFAImport";
import { VenueForm, emptyVenue } from "@/components/admin/VenueForm";
import { PitchForm, emptyPitch } from "@/components/admin/PitchForm";
import { parseCSV } from "@/lib/csv";
import { AuditTab } from "@/pages/admin/AuditTab";
import { EmailsTab } from "@/pages/admin/EmailsTab";
import { BookingsTab } from "@/pages/admin/BookingsTab";
import { DismissibleBanner, restoreAllBanners } from "@/components/app/DismissibleBanner";
import { RefOverdueTile } from "@/components/admin/RefOverdueTile";
import { SecretaryTab } from "@/pages/admin/SecretaryTab";
import { ProposalsTab } from "@/pages/admin/ProposalsTab";
import { ApprovalRoutingTab } from "@/pages/admin/ApprovalRoutingTab";
import { WaitingListEmailsTab } from "@/pages/admin/WaitingListEmailsTab";
import { PublicApiTab } from "@/pages/admin/PublicApiTab";
import { RolesTab } from "@/pages/admin/RolesTab";
import { IncidentFormsTab } from "@/pages/admin/IncidentFormsTab";
import { ReleasesTab } from "@/pages/admin/ReleasesTab";
import { CommitteeTab } from "@/pages/admin/CommitteeTab";
import { CommitteeContactsTab } from "@/pages/admin/CommitteeContactsTab";
import { LeagueExportTab } from "@/pages/admin/LeagueExportTab";
import { AccessRequestsTab } from "@/pages/admin/AccessRequestsTab";
import { ProductCatalogueTab } from "@/pages/admin/ProductCatalogueTab";
import { FeedbackTab } from "@/pages/admin/FeedbackTab";
import { InboxTab } from "@/pages/admin/InboxTab";
import { ChatOversightTab } from "@/pages/admin/ChatOversightTab";
import { AdminGroupNav, ADMIN_TAB_LABELS } from "@/components/admin/AdminGroupNav";
import { UtilisationTab } from "@/pages/admin/UtilisationTab";
import { UsersTab } from "@/pages/admin/UsersTab";
import { UserPicker } from "@/components/app/UserPicker";
import { PaymentsTab } from "@/pages/admin/PaymentsTab";
import { FeaturesTab } from "@/pages/admin/FeaturesTab";

const REFEREE_AGES = ["U7","U8","U9","U10","U11","U12","U13","U14","U15","U16","U18","Adults"];
const emptyRef = { name: "", phone: "", email: "", level: "", is_qualified: false, notify_enabled: true, is_club_member: false, allow_concurrent: false, max_age_group: "", available_saturday: true, available_sunday: true, parent_name: "", parent_phone: "", parent_email: "" };

export default function Admin() {
  const { user, impersonate } = useAuth();
  const nav = useNavigate();
  const { tab: pathTab } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const adminTab = pathTab || "venues";
  // Legacy deep links (/app/admin/x) → path tabs; unknown tab names → dashboard.
  useEffect(() => {
    const legacy = searchParams.get("tab");
    if (legacy) { const n = new URLSearchParams(searchParams); n.delete("tab"); nav(`/app/admin/${legacy}${n.toString() ? `?${n}` : ""}`, { replace: true }); return; }
    if (pathTab && !ADMIN_TAB_LABELS[pathTab]) nav("/app/admin", { replace: true });
  }, [pathTab, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps
  const setAdminTab = (t) => {
    if (t.startsWith("/")) { nav(t); return; }
    nav(t === "venues" ? "/app/admin" : `/app/admin/${t}`);
  };
  const [stats, setStats] = useState(null);
  const statsSafe = stats || {};
  const [bookings, setBookings] = useState([]);
  const [users, setUsers] = useState([]);
  const [venues, setVenues] = useState([]);
  const [referees, setReferees] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [settings, setSettings] = useState({ referee_notifications: true, booking_email_notifications: true, weekly_recap_enabled: false, weekly_recap_recipients: [], coach_compliance_reminders_enabled: false, self_registration_enabled: false, midweek_open_hour: 16, midweek_close_hour: 21, weekend_open_hour: 8, weekend_close_hour: 17, winter_training_start: "", summer_training_start: "", season_end_date: "", secretary_cadence: "realtime", secretary_custom_days: [], secretary_notices_enabled: true });
  const [watchlist, setWatchlist] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [openSeason, setOpenSeason] = useState(false);
  const [seasonForm, setSeasonForm] = useState({ name: "", start_date: "", end_date: "" });
  const [openRollover, setOpenRollover] = useState(false);
  const [rolloverForm, setRolloverForm] = useState({ new_season_name: "", start_date: "", end_date: "", copy_leagues: true, copy_teams: true, copy_players: false });
  const emptyContact = { name: "", phone: "", email: "" };
  const emptyLeagueForm = { name: "", website: "", notes: "", league_contact: {...emptyContact}, chairman: {...emptyContact}, secretary: {...emptyContact}, welfare_officer: {...emptyContact}, fixture_secretary: {...emptyContact}, league_fee: "", default_qualified_fee: "", default_unqualified_fee: "", payment_grid: [], fa_fixtures_url: "", publish_results: false, publish_table: false };
  const [leagues, setLeagues] = useState([]);
  const [openLeague, setOpenLeague] = useState(false);
  const [editingLeague, setEditingLeague] = useState(null);
  const [leagueForm, setLeagueForm] = useState(emptyLeagueForm);
  const [faRefreshing, setFaRefreshing] = useState(false);
  const [lastFaDiff, setLastFaDiff] = useState(null);
  const [openVenue, setOpenVenue] = useState(false);
  const [editingVenue, setEditingVenue] = useState(null);
  const [venueForm, setVenueForm] = useState(emptyVenue);
  const [openPitch, setOpenPitch] = useState(false);
  const [editingPitch, setEditingPitch] = useState(null);
  const [pitchForm, setPitchForm] = useState(emptyPitch);
  const [openRef, setOpenRef] = useState(false);
  const [editingRef, setEditingRef] = useState(null);
  const [refForm, setRefForm] = useState(emptyRef);
  const [uploadRows, setUploadRows] = useState([]);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [selectedRefId, setSelectedRefId] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [openNewUser, setOpenNewUser] = useState(false);
  const [newUserForm, setNewUserForm] = useState({ email: "", name: "", role: "coach", phone: "", password: "", force_password_change: true, send_welcome_email: true });
  const [reassignSrc, setReassignSrc] = useState(null);
  const [reassignTo, setReassignTo] = useState("");
  const [reassignBusy, setReassignBusy] = useState(false);
  const [newUserResult, setNewUserResult] = useState(null);
  const submitNewUser = async () => {
    if (!newUserForm.email || !newUserForm.name) return toast.error("Name and email required");
    const payload = {
      email: newUserForm.email.trim().toLowerCase(),
      name: newUserForm.name.trim(),
      role: newUserForm.role,
      phone: newUserForm.phone || null,
      password: newUserForm.password || null,
      force_password_change: !!newUserForm.force_password_change,
    };
    try {
      const r = await api.post("/admin/users", payload);
      setNewUserResult(r.data);
      toast.success(`${payload.name} added`);
      loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const [kitLabels, setKitLabels] = useState({Black:"Black", White:"White", Green:"Green", Yellow:"Yellow", Blue:"Blue"});
  useEffect(() => { api.get("/settings/kit-labels").then(r => setKitLabels({...{Black:"Black",White:"White",Green:"Green",Yellow:"Yellow",Blue:"Blue"}, ...(r.data.labels||{})})).catch(()=>{}); }, []);
  const saveKitLabels = async () => {
    try {
      await api.put("/settings/kit-labels", { labels: kitLabels });
      toast.success("Kit labels updated");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const [audit, setAudit] = useState(null);
  const [auditBusy, setAuditBusy] = useState(false);
  const [restoringAuditId, setRestoringAuditId] = useState(null);
  const loadAudit = async () => {
    setAuditBusy(true);
    try { const { data } = await api.get("/admin/audit-log"); setAudit(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setAuditBusy(false); }
  };
  const auditIsRestorable = (a) => {
    if (a.reverted) return false;
    if (a.action === "delete") return true;
    if (a.action === "cancel" && a.entity_type === "booking") return true;
    if (a.action === "update" && a.payload_before) return true;
    return false;
  };
  const restoreAudit = async (a) => {
    const label = a.action === "cancel"
      ? `Re-confirm booking "${a.entity_name || ""}"?`
      : a.action === "update"
      ? `Revert this update to ${a.entity_type} "${a.entity_name || ""}"?`
      : `Restore ${a.entity_type} "${a.entity_name || ""}"?`;
    if (!window.confirm(label)) return;
    setRestoringAuditId(a.id);
    try {
      await api.post(`/admin/audit-log/${a.id}/restore`);
      toast.success("Restored");
      await loadAudit();
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setRestoringAuditId(null);
    }
  };
  const [emails, setEmails] = useState(null);
  const [emailsBusy, setEmailsBusy] = useState(false);
  const loadEmails = async () => {
    setEmailsBusy(true);
    try { const { data } = await api.get("/admin/email-log"); setEmails(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setEmailsBusy(false); }
  };
  // Records tabs fetch their own data only when opened.
  useEffect(() => { if (adminTab === "audit") loadAudit(); if (adminTab === "emails") loadEmails(); }, [adminTab]); // eslint-disable-line react-hooks/exhaustive-deps
  const _today = new Date();
  const _monthAgo = new Date(); _monthAgo.setDate(_monthAgo.getDate() - 30);
  const _fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
  const [paymentsRange, setPaymentsRange] = useState({ start: _fmt(_monthAgo), end: _fmt(_today) });
  const [payments, setPayments] = useState(null);
  const [paymentsBusy, setPaymentsBusy] = useState(false);
  const loadPayments = async () => {
    setPaymentsBusy(true);
    try {
      const { data } = await api.get("/admin/referee-payments", { params: paymentsRange });
      setPayments(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setPaymentsBusy(false); }
  };

  const loadAll = async () => {
    // Shared, lightweight reference data for every tab; heavy per-tab datasets load lazily below.
    const results = await Promise.allSettled([
      api.get("/venues", { params: { include_inactive: true } }),
      api.get("/referees"),
      api.get("/admin/settings"),
      api.get("/leagues"),
      api.get("/seasons"),
    ]);
    const setters = [setVenues, setReferees, setSettings, setLeagues, setSeasons];
    results.forEach((r, i) => { if (r.status === "fulfilled") setters[i](r.value.data); });
    loadTabData(adminTab);
  };
  const loadTabData = (t) => {
    const need = (cond, path, setter, params) => { if (cond) api.get(path, params ? { params } : undefined).then(r => setter(r.data)).catch(() => {}); };
    need(t === "venues" || t === "dashboard", "/admin/stats", setStats);
    need(t === "venues" || t === "dashboard" || t === "coaches" || t === "people", "/admin/coaches/compliance-watchlist", setWatchlist);
    need(["venues", "dashboard", "bookings", "clashes", "fixtures", "secretary", "swaps"].includes(t), "/bookings", setBookings);
    need(["venues", "dashboard", "users", "coaches", "roles", "committee", "access-requests", "proposals", "people"].includes(t) || true, "/admin/users", setUsers);
    need(t === "fixtures" || t === "venues", "/admin/fixtures/uploads", setUploads);
  };
  useEffect(() => { loadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadTabData(adminTab); }, [adminTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Venue
  const openCreateVenue = () => { setVenueForm(emptyVenue); setEditingVenue(null); setOpenVenue(true); };
  const openEditVenue = (v) => { setEditingVenue(v); setVenueForm({ name: v.name, short_code: v.short_code || "", address: v.address || "", contact_name: v.contact_name || "", contact_phone: v.contact_phone || "", notes: v.notes || "", photo_path: v.photo_path || null, training_seasons: v.training_seasons || [], is_unavailable: !!v.is_unavailable, unavailable_from: v.unavailable_from || "", unavailable_until: v.unavailable_until || "", unavailable_reason: v.unavailable_reason || "", closed_days: v.closed_days || [], needs_external_confirmation: !!v.needs_external_confirmation, is_game_day: !!v.is_game_day, league_earliest_hour: v.league_earliest_hour ?? "" }); setOpenVenue(true); };
  const submitVenue = async () => {
    if (!venueForm.name) return toast.error("Venue name required");
    if (venueForm.is_unavailable && venueForm.unavailable_from && venueForm.unavailable_until && venueForm.unavailable_until < venueForm.unavailable_from) return toast.error("Unavailable-Until must be on or after Unavailable-From");
    const payload = { name: venueForm.name, short_code: (venueForm.short_code || "").trim() || null, address: venueForm.address || null, contact_name: venueForm.contact_name || null, contact_phone: venueForm.contact_phone || null, notes: venueForm.notes || null, training_seasons: venueForm.training_seasons?.length ? venueForm.training_seasons : null, is_unavailable: !!venueForm.is_unavailable, unavailable_from: venueForm.is_unavailable ? (venueForm.unavailable_from || null) : null, unavailable_until: venueForm.is_unavailable ? (venueForm.unavailable_until || null) : null, unavailable_reason: venueForm.is_unavailable ? (venueForm.unavailable_reason || null) : null, closed_days: venueForm.closed_days || [], needs_external_confirmation: !!venueForm.needs_external_confirmation, is_game_day: !!venueForm.is_game_day, league_earliest_hour: venueForm.league_earliest_hour === "" || venueForm.league_earliest_hour == null ? null : Number(venueForm.league_earliest_hour) };
    try {
      let res;
      if (editingVenue) res = await api.put(`/venues/${editingVenue.id}`, payload);
      else res = await api.post("/venues", payload);
      const cancelled = res?.data?._cancelled_bookings || 0;
      toast.success(cancelled > 0 ? `Venue saved · ${cancelled} booking${cancelled === 1 ? "" : "s"} cancelled` : (editingVenue ? "Venue updated" : "Venue created"));
      setOpenVenue(false); setEditingVenue(null); setVenueForm(emptyVenue); loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const deleteVenue = async (id) => {
    if (!window.confirm("Archive this venue?")) return;
    try { await api.delete(`/venues/${id}`); toast.success("Venue archived"); loadAll(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const restoreVenue = async (id) => { await api.post(`/venues/${id}/restore`); toast.success("Venue restored"); loadAll(); };

  // Pitch
  const openCreatePitch = (venue_id = "") => { setPitchForm({...emptyPitch, venue_id}); setEditingPitch(null); setOpenPitch(true); };
  const openEditPitch = (p) => { setEditingPitch(p); setPitchForm({ name: p.name, surface: p.surface, venue_id: p.venue_id || "", capacity: p.capacity, game_format: p.game_format || "", hourly_rate: p.hourly_rate || 0, image_url: p.image_url || "", description: p.description || "", open_hour: p.open_hour, close_hour: p.close_hour, pitch_dimensions: p.pitch_dimensions || "", no_games: !!p.no_games }); setOpenPitch(true); };
  const submitPitch = async () => {
    if (!pitchForm.name || !pitchForm.venue_id) return toast.error("Name and venue required");
    if (pitchForm.close_hour <= pitchForm.open_hour) return toast.error("Closes must be after opens");
    try {
      if (editingPitch) await api.put(`/pitches/${editingPitch.id}`, pitchForm);
      else await api.post("/pitches", pitchForm);
      toast.success(editingPitch ? "Pitch updated" : "Pitch created");
      setOpenPitch(false); setEditingPitch(null); setPitchForm(emptyPitch); loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const deletePitch = async (id) => { if (!window.confirm("Archive this pitch?")) return; await api.delete(`/pitches/${id}`); toast.success("Pitch archived"); loadAll(); };
  const restorePitch = async (id) => { await api.post(`/pitches/${id}/restore`); toast.success("Pitch restored"); loadAll(); };

  // Leagues
  const cleanContact = (c) => {
    if (!c) return null;
    const out = { name: c.name || null, phone: c.phone || null, email: c.email || null };
    return (out.name || out.phone || out.email) ? out : null;
  };
  const openCreateLeague = () => { setLeagueForm(emptyLeagueForm); setEditingLeague(null); setOpenLeague(true); };
  const openEditLeague = (l) => {
    setEditingLeague(l);
    const c = (k) => ({ name: l[k]?.name || "", phone: l[k]?.phone || "", email: l[k]?.email || "" });
    setLeagueForm({
      name: l.name, website: l.website || "", notes: l.notes || "",
      league_contact: c("league_contact"), chairman: c("chairman"),
      secretary: c("secretary"), welfare_officer: c("welfare_officer"),
      fixture_secretary: c("fixture_secretary"),
      league_fee: l.league_fee ?? "",
      default_qualified_fee: l.default_qualified_fee ?? "",
      default_unqualified_fee: l.default_unqualified_fee ?? "",
      payment_grid: (l.payment_grid || []).map(r => ({
        age_group: r.age_group || "",
        duration_min: r.duration_min ?? "",
        qualified_fee: r.qualified_fee ?? "",
        unqualified_fee: r.unqualified_fee ?? "",
      })),
      fa_fixtures_url: l.fa_fixtures_url || "",
      publish_results: !!l.publish_results, publish_table: !!l.publish_table,
    });
    setOpenLeague(true);
  };
  const submitLeague = async (opts = {}) => {
    if (!leagueForm.name) return toast.error("League name required");
    const numOrNull = (v) => (v === "" || v === null || v === undefined || isNaN(Number(v))) ? null : Number(v);
    const grid = (leagueForm.payment_grid || [])
      .filter(r => (r.age_group || "").trim())
      .map(r => ({
        age_group: r.age_group.trim(),
        duration_min: numOrNull(r.duration_min),
        qualified_fee: numOrNull(r.qualified_fee) ?? 0,
        unqualified_fee: numOrNull(r.unqualified_fee) ?? 0,
      }));
    const payload = {
      name: leagueForm.name,
      website: leagueForm.website || null,
      notes: leagueForm.notes || null,
      league_contact: cleanContact(leagueForm.league_contact),
      chairman: cleanContact(leagueForm.chairman),
      secretary: cleanContact(leagueForm.secretary),
      welfare_officer: cleanContact(leagueForm.welfare_officer),
      fixture_secretary: cleanContact(leagueForm.fixture_secretary),
      league_fee: numOrNull(leagueForm.league_fee),
      default_qualified_fee: numOrNull(leagueForm.default_qualified_fee),
      default_unqualified_fee: numOrNull(leagueForm.default_unqualified_fee),
      payment_grid: grid,
      fa_fixtures_url: (leagueForm.fa_fixtures_url || "").trim() || null,
      publish_results: !!leagueForm.publish_results, publish_table: !!leagueForm.publish_table,
    };
    try {
      if (editingLeague) await api.put(`/leagues/${editingLeague.id}`, payload);
      else await api.post("/leagues", payload);
      if (!opts.silent) {
        toast.success(editingLeague ? "League updated" : "League created");
        setOpenLeague(false); setEditingLeague(null);
      }
      loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const addGridRow = () => setLeagueForm(f => ({ ...f, payment_grid: [...(f.payment_grid || []), { age_group: "", duration_min: "", qualified_fee: "", unqualified_fee: "" }] }));
  const removeGridRow = (i) => setLeagueForm(f => ({ ...f, payment_grid: f.payment_grid.filter((_, idx) => idx !== i) }));
  const setGridCell = (i, k, v) => setLeagueForm(f => ({ ...f, payment_grid: f.payment_grid.map((r, idx) => idx === i ? { ...r, [k]: v } : r) }));
  const [gridPasteOpen, setGridPasteOpen] = useState(false);
  const [gridPasteText, setGridPasteText] = useState("");
  const parseGridPaste = (text) => {
    const lines = (text || "").split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    if (lines.length === 0) return { rows: [], errors: ["No rows"] };
    // Auto-detect delimiter: tab preferred, else comma, else 2+ spaces
    const delim = lines[0].includes("\t") ? "\t" : (lines[0].includes(",") ? "," : /\s{2,}/);
    // Detect header
    const firstCells = lines[0].split(delim).map(c => c.trim().toLowerCase());
    const looksHeader = firstCells.some(c => c.includes("age") || c.includes("qual") || c.includes("dur") || c.includes("fee"));
    const dataLines = looksHeader ? lines.slice(1) : lines;
    const strip = (s) => (s || "").replace(/£/g, "").replace(/,/g, "").trim();
    const rows = [];
    const errors = [];
    dataLines.forEach((line, idx) => {
      const cells = line.split(delim).map(c => c.trim());
      if (cells.length < 3) { errors.push(`Row ${idx + 1}: need at least 3 columns`); return; }
      const age = cells[0];
      if (!age) { errors.push(`Row ${idx + 1}: missing age group`); return; }
      let duration = "", qual = "", unqual = "";
      if (cells.length === 3) {
        [qual, unqual] = [cells[1], cells[2]];
      } else {
        // 4+ columns: age, duration, qualified, unqualified
        duration = cells[1];
        qual = cells[2];
        unqual = cells[3];
      }
      const nq = Number(strip(qual));
      const nu = Number(strip(unqual));
      const nd = duration === "" ? "" : Number(strip(duration));
      if (isNaN(nq) || isNaN(nu)) { errors.push(`Row ${idx + 1}: fees must be numbers`); return; }
      if (duration !== "" && isNaN(nd)) { errors.push(`Row ${idx + 1}: duration must be a number`); return; }
      rows.push({ age_group: age, duration_min: nd === "" ? "" : String(nd), qualified_fee: String(nq), unqualified_fee: String(nu) });
    });
    return { rows, errors };
  };
  const gridPasteParsed = gridPasteText ? parseGridPaste(gridPasteText) : { rows: [], errors: [] };
  const applyGridPaste = (mode) => {
    const { rows, errors } = gridPasteParsed;
    if (rows.length === 0) { toast.error(errors[0] || "Nothing to import"); return; }
    setLeagueForm(f => ({ ...f, payment_grid: mode === "replace" ? rows : [...(f.payment_grid || []), ...rows] }));
    toast.success(`${mode === "replace" ? "Replaced" : "Appended"} ${rows.length} row${rows.length !== 1 ? "s" : ""}`);
    setGridPasteText("");
    setGridPasteOpen(false);
  };
  const deleteLeague = async (id) => {
    if (!window.confirm("Archive this league?")) return;
    try { await api.delete(`/leagues/${id}`); toast.success("League archived"); loadAll(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  // Seasons
  const currentSeason = seasons.find(s => s.is_current);
  const createSeason = async () => {
    if (!seasonForm.name) return toast.error("Season name required");
    try {
      await api.post("/seasons", { name: seasonForm.name, start_date: seasonForm.start_date || null, end_date: seasonForm.end_date || null });
      toast.success("Season created");
      setSeasonForm({ name: "", start_date: "", end_date: "" });
      setOpenSeason(false);
      loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const setSeasonCurrent = async (id) => {
    try { await api.post(`/seasons/${id}/set-current`); toast.success("Season set as current"); loadAll(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const archiveSeason = async (id) => {
    if (!window.confirm("Archive this season? Its leagues and teams stay in the database but are hidden from the default views.")) return;
    try { await api.delete(`/seasons/${id}`); toast.success("Season archived"); loadAll(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const submitRollover = async () => {
    if (!rolloverForm.new_season_name) return toast.error("New season name required");
    if (!window.confirm(`Start "${rolloverForm.new_season_name}"? All ${rolloverForm.copy_leagues ? "leagues" : ""}${rolloverForm.copy_leagues && rolloverForm.copy_teams ? " and " : ""}${rolloverForm.copy_teams ? "teams" : ""} from ${currentSeason?.name || "the current season"} will be copied over.`)) return;
    try {
      const { data } = await api.post("/seasons/rollover", {
        new_season_name: rolloverForm.new_season_name,
        start_date: rolloverForm.start_date || null,
        end_date: rolloverForm.end_date || null,
        copy_leagues: rolloverForm.copy_leagues,
        copy_teams: rolloverForm.copy_teams,
        copy_players: rolloverForm.copy_players,
      });
      toast.success(`New season live — ${data.leagues_created} leagues, ${data.teams_created} teams copied`);
      setOpenRollover(false);
      setRolloverForm({ new_season_name: "", start_date: "", end_date: "", copy_leagues: true, copy_teams: true, copy_players: false });
      loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  // Referee
  const openCreateRef = () => { setRefForm(emptyRef); setEditingRef(null); setOpenRef(true); };
  const openEditRef = (r) => {
    setEditingRef(r);
    setRefForm({
      name: r.name, phone: r.phone || "", email: r.email || "",
      level: r.level || "", is_qualified: !!r.is_qualified, notify_enabled: r.notify_enabled !== false,
      is_club_member: !!r.is_club_member, allow_concurrent: !!r.allow_concurrent, max_age_group: r.max_age_group || "",
      available_saturday: r.available_saturday !== false,
      available_sunday: r.available_sunday !== false,
      parent_name: r.parent_name || "", parent_phone: r.parent_phone || "", parent_email: r.parent_email || "",
    });
    setOpenRef(true);
  };
  const submitRef = async () => {
    if (!refForm.name) return toast.error("Name required");
    const payload = {
      name: refForm.name, phone: refForm.phone || null, email: refForm.email || null,
      level: refForm.level || null, is_qualified: !!refForm.is_qualified, notify_enabled: refForm.notify_enabled,
      is_club_member: !!refForm.is_club_member,
      allow_concurrent: !!refForm.allow_concurrent,
      max_age_group: refForm.max_age_group || null,
      available_saturday: !!refForm.available_saturday,
      available_sunday: !!refForm.available_sunday,
      parent_name: refForm.parent_name || null,
      parent_phone: refForm.parent_phone || null,
      parent_email: refForm.parent_email || null,
    };
    try {
      if (editingRef) await api.put(`/referees/${editingRef.id}`, payload);
      else await api.post("/referees", payload);
      toast.success(editingRef ? "Referee updated" : "Referee added");
      setOpenRef(false); setEditingRef(null); setRefForm(emptyRef); loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const deleteRef = async (id) => { if (!window.confirm("Remove this referee?")) return; await api.delete(`/referees/${id}`); toast.success("Referee removed"); loadAll(); };

  // Settings
  const toggleGlobalNotifications = async (val) => {
    try {
      const { data } = await api.put("/admin/settings", { referee_notifications: val });
      setSettings(data);
      toast.success(val ? "Referee notifications on" : "Referee notifications off");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const updateSetting = async (patch, successMsg) => {
    try {
      const { data } = await api.put("/admin/settings", patch);
      setSettings(data);
      if (successMsg) toast.success(successMsg);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const [recapEmailDraft, setRecapEmailDraft] = useState("");
  const addRecapRecipient = () => {
    const e = recapEmailDraft.trim().toLowerCase();
    if (!e || !e.includes("@")) return toast.error("Enter a valid email");
    if (settings.weekly_recap_recipients?.includes(e)) return toast.error("Already added");
    const next = [...(settings.weekly_recap_recipients || []), e];
    updateSetting({ weekly_recap_recipients: next }, "Recipient added");
    setRecapEmailDraft("");
  };
  const removeRecapRecipient = (e) => {
    updateSetting({ weekly_recap_recipients: (settings.weekly_recap_recipients || []).filter(x => x !== e) }, "Recipient removed");
  };

  // Uploads
  const onFile = async (e) => { const file = e.target.files?.[0]; if (!file) return; const text = await file.text(); setUploadRows(parseCSV(text)); setUploadResult(null); };
  const submitUpload = async () => {
    if (uploadRows.length === 0) return toast.error("No rows to upload");
    setUploadBusy(true);
    try {
      const { data } = await api.post("/admin/fixtures/upload", uploadRows);
      setUploadResult(data);
      toast.success(`Uploaded ${data.created} / ${data.total} fixtures`);
      loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploadBusy(false); }
  };
  const undoUpload = async (batchId) => {
    if (!window.confirm("Undo this upload? All bookings from this batch will be cancelled.")) return;
    try {
      const { data } = await api.post(`/admin/fixtures/uploads/${batchId}/undo`);
      toast.success(`Cancelled ${data.cancelled} bookings`);
      loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  // eslint-disable-next-line
  const _uploadHelpers = { onFile, submitUpload, uploadRows, uploadResult, uploadBusy };

  const [statsRange, setStatsRange] = useState("all");
  useEffect(() => { if (stats) api.get("/admin/stats", { params: { range: statsRange } }).then(r => setStats(r.data)).catch(() => {}); }, [statsRange]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!settings) return <div className="opacity-60 font-display tracking-widest">LOADING ADMIN…</div>;

  const rangeLabel = { all: "All time", past: "Past", weekend: "This weekend" }[statsRange];
  const tiles = [
    { label: statsRange === "all" ? "Total bookings" : `Bookings · ${rangeLabel}`, value: statsSafe.total_bookings, icon: CalendarIcon },
    statsRange === "all"
      ? { label: "Upcoming", value: statsSafe.upcoming_bookings, icon: TrendingUp }
      : { label: "Games / Training", value: `${statsSafe.games ?? 0} / ${statsSafe.training ?? 0}`, icon: TrendingUp },
    { label: "Members", value: statsSafe.total_users, icon: Users },
    { label: "Venues", value: venues.filter(v=>v.active).length, icon: Building2 },
    { label: "Pitches", value: statsSafe.total_pitches, icon: MapPin },
  ];

  return (
    <div>
      <div className="mb-10 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">Committee dashboard</div>
          <h1 className="font-display font-black text-4xl md:text-5xl mt-2 leading-tight">Admin</h1>
        </div>
        <Link to="/app/admin/poster" data-testid="admin-poster-link" className="btn-volt px-5 py-2.5 rounded-md inline-flex items-center gap-2 self-center">
          <Printer className="w-4 h-4" /> Weekly poster
        </Link>
      </div>

      <div className="flex flex-wrap items-center gap-1 mb-3" data-testid="admin-stats-range">
        <span className="text-[10px] tracking-widest uppercase opacity-60 mr-2">Stats</span>
        {[["all", "All"], ["past", "Past"], ["weekend", "Upcoming weekend"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setStatsRange(k)} data-testid={`admin-stats-range-${k}`}
            className={`text-[10px] tracking-widest uppercase px-3 py-1.5 rounded-full border transition-colors ${statsRange === k ? "border-[#83B68B] bg-[#83B68B]/15 text-[#83B68B]" : "border-border hover:border-white/40"}`}>{l}</button>
        ))}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-10">
        {tiles.map(t => (
          <div key={t.label} className="stat-tile p-5 rounded-md">
            <t.icon className="w-5 h-5 text-[#83B68B]" />
            <div className="font-display font-black text-3xl mt-3">{t.value}</div>
            <div className="text-[10px] tracking-[0.2em] uppercase opacity-60 mt-1">{t.label}</div>
          </div>
        ))}
      </div>

      {/* Referee TBD widget — impossible to miss red banner when league/cup games
          have no referee assigned, green all-clear when everything's covered.
          Dismissible — the count is baked into the id so a fresh alert reappears. */}
      <RefOverdueTile />
      {statsSafe.ref_tbd_count > 0 ? (
        <DismissibleBanner id={`ref-tbd-${statsSafe.ref_tbd_count}`} className="mb-10">
        <Link
          to="/app/bookings"
          data-testid="admin-ref-tbd-widget"
          className="card-tactical rounded-md border border-red-500/50 bg-red-500/[0.04] block hover:border-red-500 transition-colors"
        >
          <div className="p-5 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded bg-red-500/15 border border-red-500/40 flex items-center justify-center flex-shrink-0">
                <ShieldAlert className="w-6 h-6 text-red-400" />
              </div>
              <div className="min-w-0">
                <div className="flex items-baseline gap-3 flex-wrap">
                  <span className="font-display font-black text-3xl text-red-300" data-testid="admin-ref-tbd-count">{statsSafe.ref_tbd_count}</span>
                  <span className="font-display tracking-tight text-xl">league/cup game{statsSafe.ref_tbd_count === 1 ? "" : "s"} without a referee</span>
                </div>
                <p className="text-xs opacity-80 mt-1">
                  {statsSafe.ref_tbd_next_date
                    ? <>Next kick-off <strong>{format(parseISO(statsSafe.ref_tbd_next_date), "EEE d MMM")}</strong> — nudge the coach from the booking page.</>
                    : "Nudge the coach from the booking page."}
                </p>
              </div>
            </div>
            <span className="text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-red-500/60 text-red-300 inline-flex items-center gap-1.5 flex-shrink-0">
              Review bookings <ArrowRight className="w-3 h-3" />
            </span>
          </div>
        </Link>
        </DismissibleBanner>
      ) : (
        <DismissibleBanner id="ref-tbd-allclear" className="mb-10">
        <div
          data-testid="admin-ref-tbd-widget"
          className="card-tactical rounded-md border border-[#83B68B]/40 bg-[#83B68B]/[0.04]"
        >
          <div className="p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded bg-[#83B68B]/15 border border-[#83B68B]/40 flex items-center justify-center flex-shrink-0">
              <ShieldAlert className="w-4 h-4 text-[#83B68B]" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium">All league &amp; cup games have a referee</div>
              <p className="text-[11px] opacity-70 mt-0.5">Nothing to chase — the Thursday &amp; Friday digests will stay quiet.</p>
            </div>
          </div>
        </div>
        </DismissibleBanner>
      )}

      {watchlist && (watchlist.counts.expired > 0 || watchlist.counts.expiring > 0 || watchlist.counts.missing > 0) && (
        <DismissibleBanner id={`compliance-${watchlist.counts.expired}-${watchlist.counts.expiring}-${watchlist.counts.missing}`} className="mb-10">
        <div className="card-tactical rounded-md border border-amber-500/40" data-testid="admin-compliance-watchlist">
          <div className="p-5 border-b border-border flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded bg-amber-500/15 border border-amber-500/40 flex items-center justify-center">
                <ShieldAlert className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <div className="font-display font-bold text-xl tracking-tight">Compliance watchlist</div>
                <p className="text-xs opacity-60 mt-0.5">DBS, Safeguarding, or Intro to First Aid — expired or expiring within {watchlist.threshold_days} days.</p>
              </div>
            </div>
            <div className="flex gap-2 text-[10px] tracking-widest uppercase" data-testid="watchlist-counts">
              {watchlist.counts.expired > 0 && <span className="px-2 py-1 rounded bg-red-500/20 text-red-500 border border-red-500/40">{watchlist.counts.expired} expired</span>}
              {watchlist.counts.expiring > 0 && <span className="px-2 py-1 rounded bg-amber-500/20 text-amber-500 border border-amber-500/40">{watchlist.counts.expiring} expiring</span>}
              {watchlist.counts.missing > 0 && <span className="px-2 py-1 rounded bg-muted opacity-70 border border-border">{watchlist.counts.missing} missing date</span>}
            </div>
          </div>
          <div className="divide-y divide-border">
            {watchlist.coaches.map(c => (
              <div key={c.id} className="p-5 flex flex-wrap gap-3 items-center justify-between" data-testid={`watchlist-coach-${c.id}`}>
                <div className="min-w-0">
                  <div className="font-medium">{c.name}</div>
                  <div className="text-xs opacity-60 mt-0.5 flex flex-wrap gap-x-3">
                    {c.email && <a href={`mailto:${c.email}`} className="hover:text-[#83B68B]">✉ {c.email}</a>}
                    {c.phone && <a href={`tel:${(c.phone || "").replace(/\s+/g, "")}`} className="hover:text-[#83B68B]">📞 {c.phone}</a>}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {c.alerts.map(a => {
                    const tone = a.status === "expired" ? "bg-red-500/20 text-red-500 border-red-500/40"
                              : a.status === "expiring" ? "bg-amber-500/20 text-amber-500 border-amber-500/40"
                              : "bg-muted opacity-80 border-border";
                    const label = a.status === "expired" ? `${a.label}: expired ${Math.abs(a.days)}d ago`
                              : a.status === "expiring" ? `${a.label}: ${a.days === 0 ? "expires today" : `expires in ${a.days}d`}`
                              : `${a.label}: no expiry date on file`;
                    return <span key={a.cert} className={`text-[10px] tracking-widest uppercase px-2 py-1 rounded border ${tone}`} data-testid={`watchlist-alert-${c.id}-${a.cert}`}>{label}</span>;
                  })}
                </div>
              </div>
            ))}
          </div>
          <div className="p-3 border-t border-border text-[10px] tracking-widest uppercase opacity-60 text-center">
            <Link to="/app/coaches" className="hover:text-[#83B68B]" data-testid="watchlist-open-coaches">Open coaches directory to update →</Link>
          </div>
        </div>
        </DismissibleBanner>
      )}

      <Tabs value={adminTab} onValueChange={setAdminTab}>
        <AdminGroupNav tab={adminTab} onChange={setAdminTab} />

        <TabsContent value="releases" className="mt-6">
          <ReleasesTab />
        </TabsContent>
        <TabsContent value="feedback" className="mt-6">
          <FeedbackTab />
        </TabsContent>
        <TabsContent value="inbox" className="mt-6">
          <InboxTab />
        </TabsContent>
        <TabsContent value="chat" className="mt-6">
          <ChatOversightTab />
        </TabsContent>

        <TabsContent value="committee" className="mt-6">
          <CommitteeTab />
        </TabsContent>

        <TabsContent value="committee-contacts" className="mt-6">
          <CommitteeContactsTab />
        </TabsContent>

        <TabsContent value="league-export" className="mt-6">
          <LeagueExportTab />
        </TabsContent>

        <TabsContent value="access-requests" className="mt-6">
          <AccessRequestsTab />
        </TabsContent>

        <TabsContent value="product-catalogue" className="mt-6">
          <ProductCatalogueTab />
        </TabsContent>

        <TabsContent value="incidents" className="mt-6">
          <IncidentFormsTab />
        </TabsContent>

        <TabsContent value="features" className="mt-6">
          <FeaturesTab />
        </TabsContent>

        <TabsContent value="venues" className="mt-6">
          <div className="flex justify-between items-center mb-4 gap-3 flex-wrap">
            <div className="text-sm opacity-70">{venues.filter(v=>v.active).length} venues · {venues.reduce((a,v)=>a+(v.pitches||[]).filter(p=>p.active).length,0)} pitches</div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Jump-to-pitch — flat list of every active pitch grouped by venue,
                  selecting one scrolls the venue card into view and briefly
                  highlights the pitch tile so admins can find it fast in a big list. */}
              <Select
                value=""
                onValueChange={(pitchId) => {
                  const el = document.querySelector(`[data-pitch-card="${pitchId}"]`);
                  if (el) {
                    el.scrollIntoView({ behavior: "smooth", block: "center" });
                    el.classList.add("ring-2", "ring-[#83B68B]");
                    setTimeout(() => el.classList.remove("ring-2", "ring-[#83B68B]"), 2000);
                  }
                }}
              >
                <SelectTrigger className="h-9 min-w-[220px] text-xs" data-testid="admin-jump-to-pitch">
                  <SelectValue placeholder="Jump to pitch…" />
                </SelectTrigger>
                <SelectContent className="max-h-[400px]">
                  {venues.filter(v => v.active).map(v => (
                    <div key={v.id}>
                      <div className="px-2 py-1 text-[10px] tracking-widest uppercase opacity-60">{v.name}</div>
                      {(v.pitches || []).filter(p => p.active).map(p => (
                        <SelectItem key={p.id} value={p.id} data-testid={`admin-jump-to-pitch-${p.id}`}>{p.name}</SelectItem>
                      ))}
                    </div>
                  ))}
                </SelectContent>
              </Select>
              <label className="flex items-center gap-2 text-xs tracking-widest uppercase opacity-70 select-none cursor-pointer" data-testid="admin-toggle-archived-wrap">
                <input type="checkbox" checked={showArchived} onChange={e=>setShowArchived(e.target.checked)} data-testid="admin-toggle-archived" />
                Show archived
              </label>
              <button onClick={openCreateVenue} className="btn-volt px-5 py-2.5 rounded-md inline-flex items-center gap-2"><Plus className="w-4 h-4" /> New venue</button>
            </div>
          </div>
          <div className="space-y-6">
            {venues.filter(v => showArchived || v.active).map(v => (
              <div key={v.id} className={`card-tactical rounded-md ${!v.active ? "opacity-50" : ""}`}>
                <div className="p-5 border-b border-border flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex items-start gap-4 min-w-0">
                    <div className="w-11 h-11 rounded bg-[#83B68B]/15 border border-[#83B68B]/30 flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-5 h-5 text-[#83B68B]" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-display font-black text-xl tracking-tight">{v.name}{!v.active && <span className="ml-2 text-red-500 text-xs">archived</span>}</div>
                      {v.address && <div className="text-xs opacity-60 mt-0.5">{v.address}</div>}
                      {(v.contact_name || v.contact_phone) && <div className="text-xs opacity-60 mt-0.5">Contact: {[v.contact_name, v.contact_phone].filter(Boolean).join(" · ")}</div>}
                      {v.notes && <div className="text-xs opacity-50 mt-1 italic">{v.notes}</div>}
                      {v.training_seasons && v.training_seasons.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {v.training_seasons.includes("winter") && <span className="text-[10px] tracking-widest uppercase px-2 py-0.5 rounded bg-sky-500/10 text-sky-300 border border-sky-500/30">Winter training</span>}
                          {v.training_seasons.includes("summer") && <span className="text-[10px] tracking-widest uppercase px-2 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/30">Summer training</span>}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={()=>openEditVenue(v)} className="text-xs tracking-widest uppercase px-3 py-1.5 border border-border hover:border-[#83B68B] hover:text-[#83B68B] rounded inline-flex items-center gap-1.5 transition-colors"><Pencil className="w-3 h-3" /> Edit venue</button>
                    {v.active
                      ? <button onClick={()=>deleteVenue(v.id)} className="text-xs tracking-widest uppercase px-3 py-1.5 border border-border hover:border-red-400 hover:text-red-500 rounded inline-flex items-center gap-1.5 transition-colors"><Trash2 className="w-3 h-3" /> Archive</button>
                      : <button onClick={()=>restoreVenue(v.id)} className="text-xs tracking-widest uppercase px-3 py-1.5 border border-border hover:border-[#83B68B] hover:text-[#83B68B] rounded inline-flex items-center gap-1.5 transition-colors"><RotateCcw className="w-3 h-3" /> Restore</button>}
                  </div>
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-[10px] tracking-[0.3em] uppercase opacity-60">Pitches ({(v.pitches||[]).length})</div>
                    {v.active && <button onClick={()=>openCreatePitch(v.id)} className="text-xs tracking-widest uppercase text-[#83B68B] hover:underline inline-flex items-center gap-1.5"><Plus className="w-3 h-3" /> Add pitch</button>}
                  </div>
                  {(v.pitches || []).filter(p => showArchived || p.active).length === 0 ? <div className="text-sm opacity-50 italic">{showArchived ? "No pitches added yet." : "No active pitches. Tick 'Show archived' to see archived ones."}</div>
                    : <div className="grid sm:grid-cols-2 gap-3">
                      {v.pitches.filter(p => showArchived || p.active).map(p => (
                        <div key={p.id} data-pitch-card={p.id} className={`bg-muted border border-border rounded p-4 flex gap-3 transition-shadow ${!p.active ? "opacity-50" : ""}`}>
                          {p.image_url ? <img src={p.image_url} alt="" className="w-16 h-16 object-cover rounded flex-shrink-0" /> : <div className="w-16 h-16 rounded flex items-center justify-center opacity-40 flex-shrink-0"><Circle className="w-5 h-5" /></div>}
                          <div className="flex-1 min-w-0">
                            <div className="font-display font-bold text-sm tracking-tight truncate flex items-center gap-1.5">
                              <span className="truncate">{p.name}</span>
                              {p.no_games && <span data-testid={`pitch-training-only-badge-${p.id}`} className="text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded border border-amber-400/60 text-amber-400 whitespace-nowrap">Training only</span>}
                            </div>
                            <div className="text-[10px] tracking-widest uppercase opacity-50 mt-0.5">{p.surface} · Cap {p.capacity}{p.game_format ? ` · ${p.game_format}` : ""} · {p.open_hour.toString().padStart(2,"0")}–{p.close_hour.toString().padStart(2,"0")}</div>
                            {p.pitch_dimensions && <div className="text-[10px] tracking-widest uppercase opacity-50 mt-0.5" data-testid={`pitch-dimensions-${p.id}`}>Size · {p.pitch_dimensions}</div>}
                            <div className="mt-2 flex gap-1.5">
                              <button onClick={()=>openEditPitch(p)} className="text-[10px] tracking-widest uppercase px-2 py-1 border border-border hover:border-[#83B68B] hover:text-[#83B68B] rounded transition-colors">Edit</button>
                              {p.active
                                ? <button onClick={()=>deletePitch(p.id)} className="text-[10px] tracking-widest uppercase px-2 py-1 border border-border hover:border-red-400 hover:text-red-500 rounded transition-colors">Archive</button>
                                : <button onClick={()=>restorePitch(p.id)} className="text-[10px] tracking-widest uppercase px-2 py-1 border border-border hover:border-[#83B68B] hover:text-[#83B68B] rounded transition-colors">Restore</button>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>}
                </div>
              </div>
            ))}
          </div>

          <Dialog open={openVenue} onOpenChange={(v)=>{setOpenVenue(v); if(!v){setEditingVenue(null); setVenueForm(emptyVenue);}}}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto"><DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">{editingVenue ? "Edit venue" : "Add venue"}</DialogTitle></DialogHeader>
              <VenueForm value={venueForm} onChange={setVenueForm} venueId={editingVenue?.id} />
              <button onClick={submitVenue} className="btn-volt w-full py-3 rounded-md">{editingVenue ? "Save changes" : "Create venue"}</button>
              {editingVenue && (
                <div className="mt-6 pt-6 border-t border-border">
                  <div className="text-[10px] tracking-widest uppercase text-[#83B68B] mb-3">Venue documents</div>
                  <p className="text-xs opacity-60 mb-4">Ground safety certificates, hire agreements, fixture arrangements — anything you want kept on file for {editingVenue.name}.</p>
                  <DocumentPanel
                    listEndpoint={`/files/document/venue/${editingVenue.id}`}
                    uploadEndpoint={`/files/document/venue/${editingVenue.id}`}
                    canEdit={true}
                    emptyLabel="No documents attached to this venue yet."
                    testIdPrefix={`venue-doc-${editingVenue.id}`}
                  />
                </div>
              )}
            </DialogContent>
          </Dialog>
          <Dialog open={openPitch} onOpenChange={(v)=>{setOpenPitch(v); if(!v){setEditingPitch(null); setPitchForm(emptyPitch);}}}>
            <DialogContent className="max-w-2xl"><DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">{editingPitch ? "Edit pitch" : "Add pitch"}</DialogTitle></DialogHeader>
              <PitchForm value={pitchForm} onChange={setPitchForm} venues={venues} />
              <button onClick={submitPitch} className="btn-volt w-full py-3 rounded-md">{editingPitch ? "Save changes" : "Create pitch"}</button>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="leagues" className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <div className="text-sm opacity-70">{leagues.length} league{leagues.length!==1 && "s"} · Teams linked automatically when a league_name is included on the Teams upload</div>
            <button onClick={openCreateLeague} className="btn-volt px-5 py-2.5 rounded-md inline-flex items-center gap-2" data-testid="admin-league-new-btn"><Plus className="w-4 h-4" /> New league</button>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {leagues.map(l => {
              const roles = [
                ["League contact", l.league_contact],
                ["Chairman", l.chairman],
                ["Secretary", l.secretary],
                ["Welfare officer", l.welfare_officer],
                ["Fixture secretary", l.fixture_secretary],
              ].filter(([, c]) => c && (c.name || c.phone || c.email));
              return (
                <div key={l.id} className="card-tactical rounded-md p-5">
                  <div className="flex justify-between items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-display font-black text-xl tracking-tight">{l.name}</div>
                      {l.website && <a href={l.website} target="_blank" rel="noreferrer" className="text-xs text-[#83B68B] hover:underline mt-1 inline-block">{l.website}</a>}
                      {l.notes && <div className="text-xs opacity-50 mt-2 italic">{l.notes}</div>}

                      {roles.length > 0 && (
                        <div className="mt-4 border-t border-border pt-3 space-y-2">
                          {roles.map(([label, c]) => (
                            <div key={label} className="text-xs">
                              <div className="text-[10px] tracking-widest uppercase opacity-50">{label}</div>
                              <div className="opacity-90 flex flex-wrap gap-x-3 mt-0.5">
                                {c.name && <span className="font-medium">{c.name}</span>}
                                {c.phone && <span className="opacity-70">📞 {c.phone}</span>}
                                {c.email && <a href={`mailto:${c.email}`} className="opacity-70 hover:text-[#83B68B] hover:underline">✉ {c.email}</a>}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {(l.league_fee != null || l.default_qualified_fee != null || l.default_unqualified_fee != null || (l.payment_grid || []).length > 0) && (
                        <div className="mt-4 border-t border-border pt-3">
                          <div className="text-[10px] tracking-widest uppercase text-[#83B68B] mb-2">Payments</div>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs opacity-80">
                            {l.league_fee != null && <span>League fee: <strong>£{l.league_fee}</strong></span>}
                            {l.default_qualified_fee != null && <span>Qualified default: <strong>£{l.default_qualified_fee}</strong></span>}
                            {l.default_unqualified_fee != null && <span>Unqualified default: <strong>£{l.default_unqualified_fee}</strong></span>}
                          </div>
                          {(l.payment_grid || []).length > 0 && (
                            <div className="mt-2 text-[11px] opacity-70" data-testid={`league-grid-${l.id}`}>
                              {l.payment_grid.length} row{l.payment_grid.length !== 1 && "s"} · e.g. {l.payment_grid.slice(0, 3).map(r => `${r.age_group}${r.duration_min ? ` (${r.duration_min}m)` : ""} £${r.qualified_fee}/£${r.unqualified_fee}`).join(" · ")}{l.payment_grid.length > 3 ? "…" : ""}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={()=>openEditLeague(l)} className="opacity-60 hover:opacity-100 hover:text-[#83B68B] p-2" data-testid={`admin-league-edit-${l.id}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={()=>deleteLeague(l.id)} className="opacity-40 hover:opacity-100 hover:text-red-500 p-2"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              );
            })}
            {leagues.length === 0 && <div className="opacity-40 text-sm">No leagues yet. Create one, then link teams to it from the Teams page or via bulk upload (league_name column).</div>}
          </div>
          <Dialog open={openLeague} onOpenChange={(v)=>{setOpenLeague(v); if(!v){setEditingLeague(null);}}}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">{editingLeague ? "Edit league" : "Add league"}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><Label className="text-xs uppercase tracking-widest opacity-70">Name</Label><Input value={leagueForm.name} onChange={e=>setLeagueForm({...leagueForm, name: e.target.value})} data-testid="league-form-name" className="mt-2" placeholder="East Herts Youth League" /></div>
                <div><Label className="text-xs uppercase tracking-widest opacity-70">Website</Label><Input value={leagueForm.website} onChange={e=>setLeagueForm({...leagueForm, website: e.target.value})} className="mt-2" placeholder="https://…" /></div>
                <div>
                  <Label className="text-xs uppercase tracking-widest opacity-70">FA Full-Time fixtures URL</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      value={leagueForm.fa_fixtures_url}
                      onChange={e=>setLeagueForm({...leagueForm, fa_fixtures_url: e.target.value})}
                      data-testid="league-form-fa-url"
                      className="flex-1"
                      placeholder="https://fulltime.thefa.com/fixtures/1/…html"
                    />
                    {editingLeague && (
                      <button
                        type="button"
                        disabled={faRefreshing}
                        data-testid="league-form-fa-refresh"
                        onClick={async () => {
                          if (!(leagueForm.fa_fixtures_url || "").trim()) return toast.error("Add a URL first");
                          // Save whatever the user has typed so refresh matches the current input.
                          try {
                            setFaRefreshing(true);
                            await submitLeague({ silent: true });
                            const { data } = await api.post(`/admin/leagues/${editingLeague.id}/fa-refresh`);
                            setLastFaDiff(data);
                            toast.success(`FA sync: ${data.new.length} new · ${data.changed.length} changed · ${data.unchanged.length} unchanged · ${data.cancelled.length} cancelled`);
                          } catch (err) {
                            toast.error(formatApiError(err.response?.data?.detail) || "FA refresh failed");
                          } finally {
                            setFaRefreshing(false);
                          }
                        }}
                        className="text-[10px] tracking-widest uppercase px-3 py-2 border border-[#83B68B] text-[#83B68B] rounded hover:bg-[#83B68B]/10 disabled:opacity-50 whitespace-nowrap"
                      >
                        {faRefreshing ? "Refreshing…" : "Refresh now"}
                      </button>
                    )}
                  </div>
                  <div className="text-[10px] opacity-60 mt-1">Nightly cron will refresh every league with a saved URL. Bookings that still match the FA date + kickoff are left alone.</div>
                  <div className="mt-4 rounded border border-border p-3 space-y-2" data-testid="league-form-fa-results-block">
                    <div className="text-xs uppercase tracking-widest opacity-70">Results &amp; league tables (website publication)</div>
                    <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={!!leagueForm.publish_results} onChange={e=>setLeagueForm({...leagueForm, publish_results: e.target.checked})} className="accent-[#83B68B]" data-testid="league-form-publish-results" /> Allow this league's results to be published via the website API</label>
                    <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={!!leagueForm.publish_table} onChange={e=>setLeagueForm({...leagueForm, publish_table: e.target.checked})} className="accent-[#83B68B]" data-testid="league-form-publish-table" /> Allow this league's tables to be published via the website API</label>
                    <div className="text-[10px] opacity-60">OFF by default — some leagues (e.g. Cambridge Mini League) don't permit it. The actual FA results / table URLs are set per team (each team sits in its own division) under Teams → edit team; they refresh every evening.</div>
                  </div>
                  {lastFaDiff && lastFaDiff.league_id === editingLeague?.id && (
                    <div className="mt-3 p-3 rounded border border-border bg-muted text-xs space-y-2" data-testid="league-form-fa-diff">
                      <div className="flex flex-wrap gap-2">
                        <span className="px-2 py-0.5 rounded bg-[#83B68B]/15 text-[#83B68B] border border-[#83B68B]/40">New · {lastFaDiff.new.length}</span>
                        <span className="px-2 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/40">Changed · {lastFaDiff.changed.length}</span>
                        <span className="px-2 py-0.5 rounded bg-muted opacity-60 border border-border">Unchanged · {lastFaDiff.unchanged.length}</span>
                        <span className="px-2 py-0.5 rounded bg-red-500/15 text-red-400 border border-red-500/40">Cancelled · {lastFaDiff.cancelled.length}</span>
                      </div>
                      {lastFaDiff.changed.slice(0, 5).map(f => (
                        <div key={f.fa_id} className="opacity-80">
                          <strong>{f.home_team} vs {f.away_team}</strong> — was {f.previous_snapshot?.date} {f.previous_snapshot?.kickoff}, now <span className="text-amber-300">{f.date} {f.kickoff}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div><Label className="text-xs uppercase tracking-widest opacity-70">Notes</Label><Textarea rows={2} value={leagueForm.notes} onChange={e=>setLeagueForm({...leagueForm, notes: e.target.value})} className="mt-2" /></div>

                {[
                  ["league_contact", "League contact"],
                  ["chairman", "Chairman"],
                  ["secretary", "Secretary"],
                  ["welfare_officer", "Welfare officer"],
                  ["fixture_secretary", "Fixture secretary"],
                ].map(([key, label]) => (
                  <div key={key} className="p-4 bg-muted rounded border border-border">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B] mb-3">{label}</div>
                    <div className="grid sm:grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs opacity-70">Name</Label>
                        <Input value={leagueForm[key]?.name || ""} onChange={e=>setLeagueForm({...leagueForm, [key]: {...leagueForm[key], name: e.target.value}})} data-testid={`league-${key}-name`} className="mt-1.5" />
                      </div>
                      <div>
                        <Label className="text-xs opacity-70">Phone</Label>
                        <Input value={leagueForm[key]?.phone || ""} onChange={e=>setLeagueForm({...leagueForm, [key]: {...leagueForm[key], phone: e.target.value}})} data-testid={`league-${key}-phone`} className="mt-1.5" placeholder="07700 900000" />
                      </div>
                      <div>
                        <Label className="text-xs opacity-70">Email</Label>
                        <Input type="email" value={leagueForm[key]?.email || ""} onChange={e=>setLeagueForm({...leagueForm, [key]: {...leagueForm[key], email: e.target.value}})} data-testid={`league-${key}-email`} className="mt-1.5" placeholder="name@example.com" />
                      </div>
                    </div>
                  </div>
                ))}

                <div className="p-4 bg-muted rounded border border-border">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B] mb-3">Payments</div>
                  <div className="grid sm:grid-cols-3 gap-3">
                    <div><Label className="text-xs opacity-70">League fee (season, £)</Label><Input type="number" step="0.01" min="0" value={leagueForm.league_fee} onChange={e=>setLeagueForm({...leagueForm, league_fee: e.target.value})} data-testid="league-form-league-fee" className="mt-1.5" placeholder="e.g. 250" /></div>
                    <div><Label className="text-xs opacity-70">Default qualified (£)</Label><Input type="number" step="0.01" min="0" value={leagueForm.default_qualified_fee} onChange={e=>setLeagueForm({...leagueForm, default_qualified_fee: e.target.value})} data-testid="league-form-default-qual" className="mt-1.5" placeholder="e.g. 30" /></div>
                    <div><Label className="text-xs opacity-70">Default unqualified (£)</Label><Input type="number" step="0.01" min="0" value={leagueForm.default_unqualified_fee} onChange={e=>setLeagueForm({...leagueForm, default_unqualified_fee: e.target.value})} data-testid="league-form-default-unqual" className="mt-1.5" placeholder="e.g. 20" /></div>
                  </div>

                  <div className="mt-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[10px] tracking-widest uppercase opacity-70">Age-group grid</div>
                      <div className="flex gap-2">
                        <button type="button" onClick={()=>setGridPasteOpen(v=>!v)} data-testid="league-grid-paste-toggle" className={`text-[10px] tracking-widest uppercase px-2 py-1 border ${gridPasteOpen ? "border-[#83B68B] text-[#83B68B]" : "border-border hover:border-[#83B68B] hover:text-[#83B68B]"} rounded inline-flex items-center gap-1 transition-colors`}>
                          <Upload className="w-3 h-3" /> Paste grid
                        </button>
                        <button type="button" onClick={addGridRow} data-testid="league-grid-add" className="text-[10px] tracking-widest uppercase px-2 py-1 border border-border hover:border-[#83B68B] hover:text-[#83B68B] rounded inline-flex items-center gap-1 transition-colors"><Plus className="w-3 h-3" /> Add row</button>
                      </div>
                    </div>

                    {gridPasteOpen && (
                      <div className="mb-3 p-3 rounded border border-[#83B68B]/30 bg-[#83B68B]/5" data-testid="league-grid-paste-panel">
                        <div className="text-[10px] opacity-70 mb-2">
                          Paste from Excel / Google Sheets or type below. Columns (in order):
                          <br />
                          <strong className="opacity-90">3 cols:</strong> Age, Qualified £, Unqualified £ &nbsp; · &nbsp; <strong className="opacity-90">4 cols:</strong> Age, Duration (min), Qualified £, Unqualified £
                          <br />
                          Comma or tab separated. Header row optional. Currency symbols are ignored.
                        </div>
                        <Textarea
                          rows={5}
                          value={gridPasteText}
                          onChange={e=>setGridPasteText(e.target.value)}
                          data-testid="league-grid-paste-text"
                          placeholder={"U10,60,25,15\nU11,60,30,20\nU12,75,35,22"}
                          className="font-mono text-xs"
                        />
                        {gridPasteText && (
                          <div className="mt-2 text-[11px]">
                            <div className="opacity-70">
                              Detected {gridPasteParsed.rows.length} row{gridPasteParsed.rows.length !== 1 && "s"}
                              {gridPasteParsed.errors.length > 0 && <span className="text-red-500"> · {gridPasteParsed.errors.length} skipped</span>}
                            </div>
                            {gridPasteParsed.rows.length > 0 && (
                              <div className="mt-1 opacity-80" data-testid="league-grid-paste-preview">
                                {gridPasteParsed.rows.slice(0, 4).map((r, i) => (
                                  <span key={`${r.age_group}-${r.duration_min}-${i}`} className="inline-block mr-3">{r.age_group}{r.duration_min ? ` (${r.duration_min}m)` : ""}: £{r.qualified_fee}/£{r.unqualified_fee}</span>
                                ))}
                                {gridPasteParsed.rows.length > 4 && <span className="opacity-60">…</span>}
                              </div>
                            )}
                            {gridPasteParsed.errors.length > 0 && (
                              <ul className="mt-1 text-red-500/80 list-disc list-inside">
                                {gridPasteParsed.errors.slice(0, 4).map((e, i) => <li key={`grid-err-${i}-${e.slice(0,16)}`}>{e}</li>)}
                              </ul>
                            )}
                          </div>
                        )}
                        <div className="mt-3 flex flex-wrap gap-2">
                          <button type="button" onClick={()=>applyGridPaste("append")} disabled={gridPasteParsed.rows.length === 0} data-testid="league-grid-paste-append" className="text-xs tracking-widest uppercase px-3 py-1.5 border border-border hover:border-[#83B68B] hover:text-[#83B68B] disabled:opacity-40 disabled:pointer-events-none rounded transition-colors">Append</button>
                          <button type="button" onClick={()=>applyGridPaste("replace")} disabled={gridPasteParsed.rows.length === 0} data-testid="league-grid-paste-replace" className="text-xs tracking-widest uppercase px-3 py-1.5 bg-[#83B68B] text-white hover:brightness-95 disabled:opacity-40 disabled:pointer-events-none rounded">Replace grid</button>
                          <button type="button" onClick={()=>{setGridPasteText(""); setGridPasteOpen(false);}} className="text-xs tracking-widest uppercase px-3 py-1.5 opacity-60 hover:opacity-100 rounded">Cancel</button>
                        </div>
                      </div>
                    )}

                    {(leagueForm.payment_grid || []).length === 0 ? (
                      <div className="text-xs opacity-50 italic">No age-group overrides. Bookings will use the defaults above.</div>
                    ) : (
                      <div className="space-y-2">
                        <div className="grid grid-cols-12 gap-2 text-[10px] tracking-widest uppercase opacity-50">
                          <div className="col-span-3">Age group</div>
                          <div className="col-span-3">Duration (min)</div>
                          <div className="col-span-2">Qualified £</div>
                          <div className="col-span-2">Unqualified £</div>
                          <div className="col-span-2"></div>
                        </div>
                        {leagueForm.payment_grid.map((row, i) => (
                          <div key={row._key || `pg-${i}`} className="grid grid-cols-12 gap-2" data-testid={`league-grid-row-${i}`}>
                            <Input className="col-span-3" placeholder="U11" value={row.age_group} onChange={e=>setGridCell(i, "age_group", e.target.value)} data-testid={`league-grid-age-${i}`} />
                            <Input className="col-span-3" type="number" min="0" placeholder="60" value={row.duration_min} onChange={e=>setGridCell(i, "duration_min", e.target.value)} />
                            <Input className="col-span-2" type="number" step="0.01" min="0" placeholder="30" value={row.qualified_fee} onChange={e=>setGridCell(i, "qualified_fee", e.target.value)} data-testid={`league-grid-qual-${i}`} />
                            <Input className="col-span-2" type="number" step="0.01" min="0" placeholder="20" value={row.unqualified_fee} onChange={e=>setGridCell(i, "unqualified_fee", e.target.value)} data-testid={`league-grid-unqual-${i}`} />
                            <button type="button" onClick={()=>removeGridRow(i)} className="col-span-2 opacity-50 hover:opacity-100 hover:text-red-500 flex items-center justify-center" aria-label="Remove"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <button onClick={submitLeague} data-testid="league-form-submit" className="btn-volt w-full py-3 rounded-md">{editingLeague ? "Save changes" : "Create league"}</button>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="seasons" className="mt-6 space-y-6">
          <div className="card-tactical rounded-md p-6">
            <div className="flex justify-between items-start gap-4 flex-wrap">
              <div>
                <div className="font-display font-bold text-2xl tracking-tight">Season rollover</div>
                <p className="text-sm opacity-60 mt-1">Start a fresh season with one click. Every league and team from <strong className="text-[#83B68B]">{currentSeason?.name || "current"}</strong> is duplicated into the new season so nothing is lost — the old season is preserved in the archive.</p>
              </div>
              <button onClick={()=>{ setRolloverForm({ new_season_name: "", start_date: "", end_date: "", copy_leagues: true, copy_teams: true, copy_players: false }); setOpenRollover(true); }} className="btn-volt px-5 py-2.5 rounded-md inline-flex items-center gap-2" data-testid="admin-rollover-btn">
                <RotateCcw className="w-4 h-4" /> Start new season
              </button>
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center mb-4">
              <div className="text-sm opacity-70">{seasons.length} season{seasons.length!==1 && "s"}</div>
              <button onClick={()=>{ setSeasonForm({ name: "", start_date: "", end_date: "" }); setOpenSeason(true); }} className="text-xs tracking-widest uppercase px-3 py-2 border border-border hover:border-[#83B68B] hover:text-[#83B68B] rounded inline-flex items-center gap-1.5 transition-colors" data-testid="admin-season-new-btn">
                <Plus className="w-3 h-3" /> Add empty season
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {seasons.map(s => (
                <div key={s.id} className={`card-tactical rounded-md p-5 ${s.is_current ? "border-[#83B68B]" : ""}`}>
                  <div className="flex justify-between items-start gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="font-display font-black text-xl tracking-tight">{s.name}</div>
                        {s.is_current && <span className="text-[10px] tracking-widest uppercase bg-[#83B68B]/20 text-[#83B68B] px-2 py-0.5 rounded">Current</span>}
                      </div>
                      {(s.start_date || s.end_date) && <div className="text-xs opacity-60 mt-1">{s.start_date || "…"} — {s.end_date || "…"}</div>}
                      <div className="text-[10px] opacity-40 mt-2">Created {format(parseISO(s.created_at), "d MMM yyyy")}</div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      {!s.is_current && (
                        <button onClick={()=>setSeasonCurrent(s.id)} className="text-xs tracking-widest uppercase px-3 py-1.5 border border-border hover:border-[#83B68B] hover:text-[#83B68B] rounded transition-colors" data-testid={`admin-season-set-current-${s.id}`}>
                          Set current
                        </button>
                      )}
                      {!s.is_current && (
                        <button onClick={()=>archiveSeason(s.id)} className="opacity-40 hover:opacity-100 hover:text-red-500 p-2"><Trash2 className="w-4 h-4" /></button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Rollover dialog */}
          <Dialog open={openRollover} onOpenChange={setOpenRollover}>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">Start new season</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="p-3 bg-[#83B68B]/10 border border-[#83B68B]/30 rounded text-xs">
                  Copying from <strong>{currentSeason?.name || "current season"}</strong>. Existing leagues, teams and bookings stay intact — the new season starts with fresh copies you can edit.
                </div>
                <div><Label className="text-xs uppercase tracking-widest opacity-70">New season name</Label><Input value={rolloverForm.new_season_name} onChange={e=>setRolloverForm({...rolloverForm, new_season_name: e.target.value})} data-testid="rollover-name" className="mt-2" placeholder="2026/27" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs uppercase tracking-widest opacity-70">Start date</Label><Input type="date" value={rolloverForm.start_date} onChange={e=>setRolloverForm({...rolloverForm, start_date: e.target.value})} className="mt-2" /></div>
                  <div><Label className="text-xs uppercase tracking-widest opacity-70">End date</Label><Input type="date" value={rolloverForm.end_date} onChange={e=>setRolloverForm({...rolloverForm, end_date: e.target.value})} className="mt-2" /></div>
                </div>
                <div className="space-y-2 pt-2">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={rolloverForm.copy_leagues} onChange={e=>setRolloverForm({...rolloverForm, copy_leagues: e.target.checked})} className="accent-[#83B68B] w-4 h-4" />
                    <span className="text-sm">Copy leagues (with all contacts)</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={rolloverForm.copy_teams} onChange={e=>setRolloverForm({...rolloverForm, copy_teams: e.target.checked})} className="accent-[#83B68B] w-4 h-4" />
                    <span className="text-sm">Copy teams (name, kit colour, format, coach, league link)</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer ml-6">
                    <input type="checkbox" checked={rolloverForm.copy_players} onChange={e=>setRolloverForm({...rolloverForm, copy_players: e.target.checked})} disabled={!rolloverForm.copy_teams} className="accent-[#83B68B] w-4 h-4" />
                    <span className={`text-sm ${!rolloverForm.copy_teams ? "opacity-40" : ""}`}>Also copy player rosters</span>
                  </label>
                </div>
                <button onClick={submitRollover} data-testid="rollover-submit" className="btn-volt w-full py-3 rounded-md">Start new season</button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Add empty season dialog */}
          <Dialog open={openSeason} onOpenChange={setOpenSeason}>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">Add empty season</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs uppercase tracking-widest opacity-70">Name</Label><Input value={seasonForm.name} onChange={e=>setSeasonForm({...seasonForm, name: e.target.value})} className="mt-2" placeholder="2027/28" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs uppercase tracking-widest opacity-70">Start date</Label><Input type="date" value={seasonForm.start_date} onChange={e=>setSeasonForm({...seasonForm, start_date: e.target.value})} className="mt-2" /></div>
                  <div><Label className="text-xs uppercase tracking-widest opacity-70">End date</Label><Input type="date" value={seasonForm.end_date} onChange={e=>setSeasonForm({...seasonForm, end_date: e.target.value})} className="mt-2" /></div>
                </div>
                <button onClick={createSeason} className="btn-volt w-full py-3 rounded-md">Create season</button>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="referees" className="mt-6 space-y-8">
          <div>
            <div className="flex justify-between items-center mb-4">
              <div className="text-sm opacity-70">{referees.length} active referee{referees.length!==1 && "s"} · Fees shown per league use each referee's qualified / unqualified flag</div>
              <button onClick={openCreateRef} className="btn-volt px-5 py-2.5 rounded-md inline-flex items-center gap-2" data-testid="admin-referee-new-btn"><Plus className="w-4 h-4" /> Add referee</button>
            </div>
            <div className="grid md:grid-cols-2 gap-4" data-testid="referee-cards-grid">
              {referees.map(r => {
                const leaguesWithFees = leagues.filter(l =>
                  l.default_qualified_fee != null || l.default_unqualified_fee != null || (l.payment_grid || []).length > 0
                );
                return (
                  <div key={r.id} className="card-tactical rounded-md p-5" data-testid={`referee-card-${r.id}`}>
                    <div className="flex justify-between items-start gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="font-display font-bold text-lg tracking-tight flex items-center gap-2 flex-wrap">
                          {r.name}
                          {r.is_qualified
                            ? <span data-testid={`ref-qualified-${r.id}`} className="text-[9px] tracking-widest uppercase bg-[#83B68B]/20 text-[#83B68B] px-1.5 py-0.5 rounded border border-[#83B68B]/30">Qualified</span>
                            : <span className="text-[9px] tracking-widest uppercase bg-muted opacity-70 px-1.5 py-0.5 rounded border border-border">Unqualified</span>}
                          {r.notify_enabled ? <Bell className="w-3.5 h-3.5 text-[#83B68B]" title="Notifications on" /> : <BellOff className="w-3.5 h-3.5 opacity-40" title="Notifications off" />}
                        </div>
                        <div className="text-xs opacity-70 mt-2 space-y-0.5">
                          {r.level && <div>{r.level}</div>}
                          {r.email && <div>✉ <a href={`mailto:${r.email}`} className="hover:text-[#83B68B] hover:underline">{r.email}</a></div>}
                          {r.phone && <div>📞 {r.phone}</div>}
                          {!r.email && !r.phone && !r.level && <div className="opacity-40">No contact details</div>}
                        </div>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <button onClick={()=>openEditRef(r)} data-testid={`referee-edit-${r.id}`} className="opacity-60 hover:opacity-100 hover:text-[#83B68B] p-2"><Pencil className="w-4 h-4" /></button>
                        <button onClick={()=>deleteRef(r.id)} className="opacity-40 hover:opacity-100 hover:text-red-500 p-2"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>

                    <div className="mt-4 border-t border-border pt-3">
                      <div className="text-[10px] tracking-widest uppercase text-[#83B68B] mb-2">Fee per league</div>
                      {leaguesWithFees.length === 0 ? (
                        <div className="text-xs opacity-50 italic">No leagues have fees set yet.</div>
                      ) : (
                        <div className="space-y-1.5" data-testid={`referee-fees-${r.id}`}>
                          {leaguesWithFees.map(l => {
                            const gridRows = (l.payment_grid || []);
                            const feeOf = (row) => r.is_qualified ? row.qualified_fee : row.unqualified_fee;
                            const defaultFee = r.is_qualified ? l.default_qualified_fee : l.default_unqualified_fee;
                            const rowsSummary = gridRows.length
                              ? gridRows.slice(0, 4).map(gr => `${gr.age_group}${gr.duration_min ? ` (${gr.duration_min}m)` : ""}: £${feeOf(gr) ?? 0}`).join(" · ") + (gridRows.length > 4 ? " …" : "")
                              : null;
                            return (
                              <div key={l.id} className="text-xs">
                                <div className="flex items-baseline gap-2 flex-wrap">
                                  <span className="font-medium truncate">{l.name}</span>
                                  {defaultFee != null && <span className="text-[10px] tracking-widest uppercase bg-muted px-1.5 py-0.5 rounded">Default £{defaultFee}</span>}
                                  {defaultFee == null && !rowsSummary && <span className="text-[10px] opacity-50">— no rate</span>}
                                </div>
                                {rowsSummary && <div className="opacity-70 mt-0.5 pl-1">{rowsSummary}</div>}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {referees.length === 0 && <div className="opacity-40 text-sm">No referees yet — add one to get started.</div>}
            </div>
          </div>

          {/* Bookings by referee */}
          <div className="card-tactical rounded-md p-5" data-testid="referee-bookings-panel">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
              <div>
                <div className="font-display font-bold text-2xl tracking-tight">Referee assignments</div>
                <p className="text-sm opacity-60 mt-1">Pick a referee to see every current and future match they've been assigned to.</p>
              </div>
              <div className="w-full md:w-80">
                <Label className="text-xs uppercase tracking-widest opacity-70">Referee</Label>
                <Select value={selectedRefId} onValueChange={setSelectedRefId}>
                  <SelectTrigger data-testid="referee-select" className="mt-1.5"><SelectValue placeholder="Choose a referee" /></SelectTrigger>
                  <SelectContent>
                    {referees.map(r => <SelectItem key={r.id} value={r.id}>{r.name}{r.is_qualified ? " · Qualified" : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {!selectedRefId ? (
              <div className="opacity-50 text-sm italic">Select a referee above to view their assignments.</div>
            ) : (() => {
              const today = new Date().toISOString().slice(0,10);
              const mine = bookings.filter(b => b.referee_id === selectedRefId && b.status === "confirmed");
              const upcoming = mine.filter(b => b.booking_date >= today).sort((a,b)=> a.booking_date === b.booking_date ? a.start_hour - b.start_hour : a.booking_date.localeCompare(b.booking_date));
              const past = mine.filter(b => b.booking_date < today).sort((a,b)=> b.booking_date.localeCompare(a.booking_date));
              const totalDue = upcoming.reduce((n,b)=> n + (b.expected_referee_fee ? Number(b.expected_referee_fee) : 0), 0);
              const totalPast = past.reduce((n,b)=> n + (b.expected_referee_fee ? Number(b.expected_referee_fee) : 0), 0);
              const Row = ({ b }) => (
                <div key={b.id} className="grid grid-cols-12 gap-3 items-center border-t border-border px-3 py-2 text-xs" data-testid={`ref-booking-${b.id}`}>
                  <div className="col-span-2 font-display font-bold">{format(parseISO(b.booking_date), "d MMM yyyy")}</div>
                  <div className="col-span-2 opacity-70">{b.start_hour.toString().padStart(2,"0")}:00 — {b.end_hour.toString().padStart(2,"0")}:00</div>
                  <div className="col-span-3 truncate">{pitchLabel(b)}</div>
                  <div className="col-span-2 opacity-80 truncate">{b.team_name || "—"} vs {b.opponent || "TBC"}</div>
                  <div className="col-span-1 text-[10px] tracking-widest uppercase opacity-70">{b.match_type || "—"}</div>
                  <div className="col-span-2 text-right font-display font-black text-[#83B68B]">{b.expected_referee_fee != null ? `£${Number(b.expected_referee_fee).toFixed(2)}` : "—"}</div>
                </div>
              );
              const Header = () => (
                <div className="grid grid-cols-12 gap-3 items-center px-3 py-2 text-[10px] tracking-widest uppercase opacity-60">
                  <div className="col-span-2">Date</div>
                  <div className="col-span-2">Time</div>
                  <div className="col-span-3">Pitch</div>
                  <div className="col-span-2">Fixture</div>
                  <div className="col-span-1">Type</div>
                  <div className="col-span-2 text-right">Fee</div>
                </div>
              );
              return (
                <div className="space-y-8">
                  <div>
                    <div className="flex items-baseline justify-between mb-2">
                      <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">Upcoming ({upcoming.length})</div>
                      <div className="text-xs opacity-70">Expected total: <span className="font-display font-black text-[#83B68B]" data-testid="ref-upcoming-total">£{totalDue.toFixed(2)}</span></div>
                    </div>
                    {upcoming.length === 0 ? (
                      <div className="opacity-50 text-sm px-3 py-4 italic">No upcoming assignments.</div>
                    ) : (
                      <div className="rounded border border-border overflow-hidden">
                        <Header />
                        {upcoming.map(b => <Row key={b.id} b={b} />)}
                      </div>
                    )}
                  </div>

                  <div>
                    <div className="flex items-baseline justify-between mb-2">
                      <div className="text-[10px] tracking-[0.3em] uppercase opacity-70">Past ({past.length})</div>
                      <div className="text-xs opacity-70">Total: <span className="font-display font-black opacity-90">£{totalPast.toFixed(2)}</span></div>
                    </div>
                    {past.length === 0 ? (
                      <div className="opacity-50 text-sm px-3 py-4 italic">No past assignments.</div>
                    ) : (
                      <div className="rounded border border-border overflow-hidden opacity-90">
                        <Header />
                        {past.slice(0, 20).map(b => <Row key={b.id} b={b} />)}
                        {past.length > 20 && <div className="px-3 py-2 text-[10px] uppercase tracking-widest opacity-50 border-t border-border">Showing latest 20 of {past.length}</div>}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
          <Dialog open={openRef} onOpenChange={(v)=>{setOpenRef(v); if(!v){setEditingRef(null); setRefForm(emptyRef);}}}>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto"><DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">{editingRef ? "Edit referee" : "Add referee"}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs uppercase tracking-widest opacity-70">Name</Label><Input value={refForm.name} onChange={e=>setRefForm({...refForm, name: e.target.value})} className="mt-2" data-testid="ref-form-name" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs uppercase tracking-widest opacity-70">Level</Label><Input value={refForm.level} onChange={e=>setRefForm({...refForm, level: e.target.value})} className="mt-2" placeholder="FA Level 7" /></div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest opacity-70">Max age group</Label>
                    <Select value={refForm.max_age_group || "any"} onValueChange={v=>setRefForm({...refForm, max_age_group: v === "any" ? "" : v})}>
                      <SelectTrigger data-testid="ref-form-max-age" className="mt-2"><SelectValue placeholder="Any" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any age group</SelectItem>
                        {REFEREE_AGES.map(a => <SelectItem key={a} value={a}>Up to {a}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label className="text-xs uppercase tracking-widest opacity-70">Email</Label><Input type="email" value={refForm.email} onChange={e=>setRefForm({...refForm, email: e.target.value})} className="mt-2" placeholder="ref@example.com" /></div>
                <div><Label className="text-xs uppercase tracking-widest opacity-70">Phone</Label><Input value={refForm.phone} onChange={e=>setRefForm({...refForm, phone: e.target.value})} className="mt-2" /></div>
                <div className="flex items-center justify-between p-3 bg-muted rounded border border-border">
                  <div>
                    <div className="text-sm font-medium">Qualified referee</div>
                    <div className="text-xs opacity-60 mt-0.5">Uses the "qualified" fee from each league's payment grid. Off = "unqualified" fee.</div>
                  </div>
                  <Switch data-testid="ref-form-qualified" checked={refForm.is_qualified} onCheckedChange={(v)=>setRefForm({...refForm, is_qualified: v})} />
                </div>
                <div className="flex items-center justify-between p-3 bg-muted rounded border border-border">
                  <div>
                    <div className="text-sm font-medium">Club member</div>
                    <div className="text-xs opacity-60 mt-0.5">Flag this referee as a member of Elsenham YFC.</div>
                  </div>
                  <Switch data-testid="ref-form-club-member" checked={refForm.is_club_member} onCheckedChange={(v)=>setRefForm({...refForm, is_club_member: v})} />
                </div>
                <div className="flex items-center justify-between p-3 bg-muted rounded border border-border">
                  <div>
                    <div className="text-sm font-medium">Can cover several games at once</div>
                    <div className="text-xs opacity-60 mt-0.5">For placeholders like "League Assigned" or "Parent Helper". Named referees are blocked from two games at the same time.</div>
                  </div>
                  <Switch data-testid="ref-form-allow-concurrent" checked={refForm.allow_concurrent} onCheckedChange={(v)=>setRefForm({...refForm, allow_concurrent: v})} />
                </div>
                <div className="flex items-center justify-between p-3 bg-muted rounded border border-border">
                  <div>
                    <div className="text-sm font-medium">Send match notifications</div>
                    <div className="text-xs opacity-60 mt-0.5">Email this referee when assigned to a league/cup match</div>
                  </div>
                  <Switch checked={refForm.notify_enabled} onCheckedChange={(v)=>setRefForm({...refForm, notify_enabled: v})} />
                </div>
                <div className="p-3 bg-muted rounded border border-border">
                  <div className="text-sm font-medium mb-1">Match-day availability</div>
                  <div className="text-xs opacity-60 mb-3">Referees will only appear in the picker for days they're available.</div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm">Saturday</div>
                    <Switch data-testid="ref-form-avail-saturday" checked={refForm.available_saturday} onCheckedChange={(v)=>setRefForm({...refForm, available_saturday: v})} />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-sm">Sunday</div>
                    <Switch data-testid="ref-form-avail-sunday" checked={refForm.available_sunday} onCheckedChange={(v)=>setRefForm({...refForm, available_sunday: v})} />
                  </div>
                </div>
                <div className="border border-border rounded-md p-3">
                  <div className="text-[10px] tracking-widest uppercase text-[#83B68B] mb-2">Parent contact (for U18 referees)</div>
                  <div className="space-y-2">
                    <Input value={refForm.parent_name} onChange={e=>setRefForm({...refForm, parent_name: e.target.value})} placeholder="Parent name" data-testid="ref-form-parent-name" />
                    <Input value={refForm.parent_phone} onChange={e=>setRefForm({...refForm, parent_phone: e.target.value})} placeholder="Parent phone" data-testid="ref-form-parent-phone" />
                    <Input type="email" value={refForm.parent_email} onChange={e=>setRefForm({...refForm, parent_email: e.target.value})} placeholder="Parent email" data-testid="ref-form-parent-email" />
                  </div>
                </div>
                <button onClick={submitRef} className="btn-volt w-full py-3 rounded-md" data-testid="ref-form-submit">{editingRef ? "Save" : "Add referee"}</button>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="payments" className="mt-6 space-y-4">
          <PaymentsTab paymentsRange={paymentsRange} setPaymentsRange={setPaymentsRange} loadPayments={loadPayments} paymentsBusy={paymentsBusy} payments={payments} />
</TabsContent>

        <TabsContent value="upload" className="mt-6 space-y-6">
          <UploadPanel loadAll={loadAll} uploads={uploads} onUndo={undoUpload} />
        </TabsContent>

        <TabsContent value="fa-import" className="mt-6">
          <AdminFAImport />
        </TabsContent>

        <TabsContent value="settings" className="mt-6 space-y-6">
          <div className="card-tactical rounded-md p-6 max-w-2xl">
            <div className="font-display font-bold text-2xl tracking-tight">Club settings</div>
            <p className="text-sm opacity-60 mt-1">Global switches for the whole club.</p>
            <div className="mt-6 flex items-center justify-between p-4 bg-muted rounded border border-border">
              <div className="pr-4">
                <div className="font-medium">Real-time booking confirmations</div>
                <div className="text-xs opacity-60 mt-1">When on, the person who books a slot gets an instant email confirmation. Turn off to silence match-day flurries.</div>
              </div>
              <Switch checked={settings.booking_email_notifications} onCheckedChange={(v) => updateSetting({ booking_email_notifications: v }, v ? "Booking emails on" : "Booking emails off")} data-testid="settings-booking-emails" />
            </div>
            <div className="mt-4 flex items-center justify-between p-4 bg-muted rounded border border-border">
              <div className="pr-4">
                <div className="font-medium">Referee match notifications</div>
                <div className="text-xs opacity-60 mt-1">When on, referees receive an email whenever they're assigned to a league or cup match (individual referees can also be muted).</div>
              </div>
              <Switch checked={settings.referee_notifications} onCheckedChange={toggleGlobalNotifications} data-testid="settings-referee-notifications" />
            </div>
          </div>

          <div className="card-tactical rounded-md p-6 max-w-2xl" data-testid="settings-weekly-recap-card">
            <div className="flex items-start justify-between gap-6">
              <div>
                <div className="font-display font-bold text-2xl tracking-tight">Weekly recap email</div>
                <p className="text-sm opacity-60 mt-1">Sends every Monday at 08:00 UK time. Includes bookings created in the last 7 days plus every fixture over the next 7 days.</p>
              </div>
              <Switch checked={settings.weekly_recap_enabled} onCheckedChange={(v) => updateSetting({ weekly_recap_enabled: v }, v ? "Weekly recap on" : "Weekly recap off")} data-testid="settings-weekly-recap-enabled" />
            </div>
            <div className={settings.weekly_recap_enabled ? "mt-6 space-y-3" : "mt-6 space-y-3 opacity-60"}>
              <div className="text-[10px] tracking-widest uppercase opacity-70">Recipients (extra, on top of every admin)</div>
              <div className="flex gap-2">
                <Input
                  data-testid="settings-recap-recipient-input"
                  placeholder="secretary@elsenham.fc"
                  className="placeholder:opacity-60"
                  disabled={!settings.weekly_recap_enabled}
                  value={recapEmailDraft}
                  onChange={e => setRecapEmailDraft(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addRecapRecipient())}
                />
                <button
                  onClick={addRecapRecipient}
                  disabled={!settings.weekly_recap_enabled}
                  data-testid="settings-recap-add-btn"
                  className="text-[10px] tracking-widest uppercase font-bold px-4 py-1.5 rounded bg-[#83B68B]/10 border border-[#83B68B]/60 text-[#83B68B] hover:bg-[#83B68B]/20 disabled:opacity-40 disabled:pointer-events-none"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {(settings.weekly_recap_recipients || []).map(e => (
                  <span key={e} className="inline-flex items-center gap-2 text-xs px-2.5 py-1 rounded border border-border bg-muted">
                    {e}
                    <button onClick={() => removeRecapRecipient(e)} data-testid={`settings-recap-remove-${e}`} className="opacity-60 hover:opacity-100 hover:text-red-400">×</button>
                  </span>
                ))}
                {(settings.weekly_recap_recipients || []).length === 0 && (
                  <span className="text-xs opacity-50">Only club admins will get it.</span>
                )}
              </div>
            </div>
          </div>

          <div className="card-tactical rounded-md p-6 max-w-2xl" data-testid="settings-booking-window-card">
            <div className="font-display font-bold text-2xl tracking-tight">Booking window hours</div>
            <p className="text-sm opacity-60 mt-1">Controls the visible time range on the Venue Calendar. Each pitch's own opening hours still apply on top — this just hides slots outside the club's usual booking window.</p>
            {[
              { label: "Midweek (Mon–Fri)", openKey: "midweek_open_hour", closeKey: "midweek_close_hour", testPrefix: "midweek" },
              { label: "Weekend (Sat–Sun)", openKey: "weekend_open_hour", closeKey: "weekend_close_hour", testPrefix: "weekend" },
            ].map(({ label, openKey, closeKey, testPrefix }) => {
              const openVal = settings[openKey];
              const closeVal = settings[closeKey];
              const clampedClose = Math.max(openVal + 1, Math.min(24, closeVal));
              return (
                <div key={openKey} className="mt-6 p-4 bg-muted rounded border border-border" data-testid={`booking-window-${testPrefix}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-medium">{label}</div>
                    <div className="text-xs opacity-80 font-mono" data-testid={`booking-window-${testPrefix}-preview`}>
                      {String(openVal).padStart(2,"0")}:00 — {String(clampedClose).padStart(2,"0")}:00
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div>
                      <div className="text-[10px] tracking-widest uppercase opacity-70 mb-1.5">Start of day</div>
                      <input
                        type="range" min={0} max={23} step={1}
                        value={openVal}
                        onChange={(e) => setSettings(s => ({ ...s, [openKey]: parseInt(e.target.value) }))}
                        onMouseUp={(e) => updateSetting({ [openKey]: parseInt(e.target.value), [closeKey]: Math.max(parseInt(e.target.value) + 1, closeVal) }, `${label} start → ${e.target.value}:00`)}
                        onTouchEnd={(e) => updateSetting({ [openKey]: parseInt(e.target.value), [closeKey]: Math.max(parseInt(e.target.value) + 1, closeVal) }, `${label} start → ${e.target.value}:00`)}
                        data-testid={`booking-window-${testPrefix}-open`}
                        className="w-full accent-[#83B68B]"
                      />
                      <div className="text-[11px] opacity-60 mt-1 tabular-nums">{String(openVal).padStart(2,"0")}:00</div>
                    </div>
                    <div>
                      <div className="text-[10px] tracking-widest uppercase opacity-70 mb-1.5">End of day</div>
                      <input
                        type="range" min={openVal + 1} max={24} step={1}
                        value={clampedClose}
                        onChange={(e) => setSettings(s => ({ ...s, [closeKey]: parseInt(e.target.value) }))}
                        onMouseUp={(e) => updateSetting({ [closeKey]: parseInt(e.target.value) }, `${label} end → ${e.target.value}:00`)}
                        onTouchEnd={(e) => updateSetting({ [closeKey]: parseInt(e.target.value) }, `${label} end → ${e.target.value}:00`)}
                        data-testid={`booking-window-${testPrefix}-close`}
                        className="w-full accent-[#83B68B]"
                      />
                      <div className="text-[11px] opacity-60 mt-1 tabular-nums">{String(clampedClose).padStart(2,"0")}:00</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="card-tactical rounded-md p-6 max-w-2xl" data-testid="settings-season-dates-card">
            <div className="font-display font-bold text-2xl tracking-tight">Training seasons</div>
            <p className="text-sm opacity-60 mt-1">These dates tell the app when the club's <strong>Winter</strong> and <strong>Summer</strong> training periods run. New training bookings are auto-tagged with the season that matches the booking date — coaches can still override on the booking form.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
              <div>
                <Label className="text-[10px] tracking-widest uppercase opacity-70">Winter training starts</Label>
                <Input
                  type="date"
                  value={settings.winter_training_start || ""}
                  onChange={(e) => setSettings(s => ({ ...s, winter_training_start: e.target.value }))}
                  onBlur={(e) => updateSetting({ winter_training_start: e.target.value || null }, "Winter start saved")}
                  data-testid="settings-winter-training-start"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label className="text-[10px] tracking-widest uppercase opacity-70">Summer training starts</Label>
                <Input
                  type="date"
                  value={settings.summer_training_start || ""}
                  onChange={(e) => setSettings(s => ({ ...s, summer_training_start: e.target.value }))}
                  onBlur={(e) => updateSetting({ summer_training_start: e.target.value || null }, "Summer start saved")}
                  data-testid="settings-summer-training-start"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label className="text-[10px] tracking-widest uppercase opacity-70">Season end date</Label>
                <Input
                  type="date"
                  value={settings.season_end_date || ""}
                  onChange={(e) => setSettings(s => ({ ...s, season_end_date: e.target.value }))}
                  onBlur={(e) => updateSetting({ season_end_date: e.target.value || null }, "Season end saved")}
                  data-testid="settings-season-end-date"
                  className="mt-1.5"
                />
              </div>
            </div>
            <div className="text-[10px] opacity-50 mt-3 leading-relaxed">Winter runs from the winter start up to (but not including) the summer start; Summer runs the other way. Leave blank to skip auto-classification.</div>
          </div>

          <div className="card-tactical rounded-md p-6 max-w-2xl" data-testid="settings-restore-banners-card">
            <div className="flex items-start justify-between gap-6">
              <div>
                <div className="font-display font-bold text-2xl tracking-tight">Hidden banners</div>
                <p className="text-sm opacity-60 mt-1">Any dashboard banner you've dismissed (Referee TBD, Compliance watchlist, Bulk convert) is remembered on this device. Click restore to bring them all back.</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  const n = restoreAllBanners();
                  if (n > 0) { toast.success(`Restored ${n} banner${n === 1 ? "" : "s"} — reload to see them`); }
                  else { toast.info("No hidden banners to restore"); }
                }}
                data-testid="settings-restore-banners-btn"
                className="px-4 py-2 rounded-md border border-[#83B68B]/50 text-[#83B68B] hover:bg-[#83B68B]/10 text-xs tracking-widest uppercase font-display font-bold whitespace-nowrap"
              >
                Restore all
              </button>
            </div>
          </div>

          <div className="card-tactical rounded-md p-6 max-w-2xl" data-testid="settings-secretary-enabled-card">
            <div className="flex items-start justify-between gap-6">
              <div>
                <div className="font-display font-bold text-2xl tracking-tight">Club Secretary notices</div>
                <p className="text-sm opacity-60 mt-1">When ON, coaches are prompted to file a league notice after cancelling or amending a league/cup game, and the Club Secretary receives an email (per the cadence above). Turn OFF to hide the prompt entirely and stop sending notices — the endpoint will refuse new filings.</p>
              </div>
              <Switch checked={!!settings.secretary_notices_enabled} onCheckedChange={(v) => updateSetting({ secretary_notices_enabled: v }, v ? "Club Secretary notices ON" : "Club Secretary notices OFF")} data-testid="settings-secretary-notices-enabled" />
            </div>
          </div>

          <div className="card-tactical rounded-md p-6 max-w-2xl" data-testid="settings-secretary-cadence-card">
            <div className="flex items-start justify-between gap-6 mb-4">
              <div>
                <div className="font-display font-bold text-2xl tracking-tight">Club secretary cadence</div>
                <p className="text-sm opacity-60 mt-1">How often the Elsenham Club Secretary receives the league-notice email. <strong>Realtime</strong> fires the moment a coach files a cancel / amend notice; the others bundle every pending notice into a daily 18:00 UK digest that only actually sends on the matching weekday(s).</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
              {[
                { v: "realtime", label: "Realtime · send instantly" },
                { v: "daily",    label: "Daily · every evening" },
                { v: "weekly",   label: "Weekly · Monday only" },
                { v: "custom",   label: "Custom · pick weekdays" },
              ].map(opt => (
                <label
                  key={opt.v}
                  className={`p-3 rounded border cursor-pointer text-xs ${settings.secretary_cadence === opt.v ? "border-[#83B68B] bg-[#83B68B]/10" : "border-border hover:border-[#83B68B]/50"}`}
                  data-testid={`settings-secretary-cadence-${opt.v}`}
                >
                  <div className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={settings.secretary_cadence === opt.v}
                      onChange={() => updateSetting({ secretary_cadence: opt.v }, `Secretary cadence · ${opt.v}`)}
                      className="accent-[#83B68B]"
                    />
                    <span className="font-semibold tracking-wide uppercase text-[10px]">{opt.label}</span>
                  </div>
                </label>
              ))}
            </div>
            {settings.secretary_cadence === "custom" && (
              <div data-testid="settings-secretary-custom-days">
                <div className="text-[10px] tracking-widest uppercase opacity-70 mb-2">Send on these days</div>
                <div className="flex flex-wrap gap-2">
                  {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d, idx) => {
                    const selected = (settings.secretary_custom_days || []).includes(idx);
                    return (
                      <button
                        key={d}
                        type="button"
                        onClick={() => {
                          const cur = settings.secretary_custom_days || [];
                          const next = selected ? cur.filter(x => x !== idx) : [...cur, idx].sort();
                          updateSetting({ secretary_custom_days: next }, `Custom days · ${next.length}`);
                        }}
                        data-testid={`settings-secretary-day-${d.toLowerCase()}`}
                        className={`px-3 py-1.5 rounded text-[10px] tracking-widest uppercase border transition-colors ${selected ? "border-[#83B68B] bg-[#83B68B]/15 text-[#83B68B]" : "border-border hover:border-white/40"}`}
                      >
                        {d}
                      </button>
                    );
                  })}
                </div>
                {(settings.secretary_custom_days || []).length === 0 && (
                  <p className="text-[11px] opacity-60 mt-2 text-amber-300">Pick at least one weekday or the digest cron will silently skip every run.</p>
                )}
              </div>
            )}
          </div>

          <div className="card-tactical rounded-md p-6 max-w-2xl" data-testid="settings-self-registration-card">
            <div className="flex items-start justify-between gap-6">
              <div>
                <div className="font-display font-bold text-2xl tracking-tight">Self-registration</div>
                <p className="text-sm opacity-60 mt-1">When OFF, the public "Create account" links are hidden and <code className="opacity-80">/register</code> is blocked. Admins can still create accounts from the Users tab. Leave OFF for private clubs.</p>
              </div>
              <Switch checked={!!settings.self_registration_enabled} onCheckedChange={(v) => updateSetting({ self_registration_enabled: v }, v ? "Self-registration ON" : "Self-registration OFF")} data-testid="settings-self-registration-enabled" />
            </div>
          </div>

          <div className="card-tactical rounded-md p-6 max-w-2xl" data-testid="settings-compliance-reminders-card">
            <div className="flex items-start justify-between gap-6">
              <div>
                <div className="font-display font-bold text-2xl tracking-tight">Coach compliance reminders</div>
                <p className="text-sm opacity-60 mt-1">Auto-emails each coach (and every admin) when a DBS, Safeguarding, or Intro to First Aid certificate hits 30 / 14 / 0 days to expiry. Runs daily at 07:00 UK. Individual coaches can be opted out from their profile.</p>
              </div>
              <Switch checked={settings.coach_compliance_reminders_enabled} onCheckedChange={(v) => updateSetting({ coach_compliance_reminders_enabled: v }, v ? "Compliance reminders on" : "Compliance reminders off")} data-testid="settings-compliance-reminders-enabled" />
            </div>
          </div>

          <div className="card-tactical rounded-md p-6 max-w-2xl" data-testid="kit-labels-card">
            <div className="font-display font-bold text-2xl tracking-tight">Team kit legend labels</div>
            <p className="text-sm opacity-60 mt-1">Rename any kit colour on the Venue Calendar legend. Bookings still store the underlying colour value; only the displayed label changes.</p>
            <div className="mt-5 space-y-3">
              {["Black","White","Green","Yellow","Blue"].map(k => {
                const swatch = { Black: "bg-black border-white/30", White: "bg-white border-black/20", Green: "bg-emerald-500 border-emerald-700", Yellow: "bg-yellow-400 border-yellow-600", Blue: "bg-blue-500 border-blue-700" }[k];
                return (
                  <div key={k} className="grid grid-cols-12 gap-3 items-center">
                    <div className="col-span-4 flex items-center gap-3">
                      <span className={`w-6 h-6 rounded border ${swatch}`} />
                      <span className="text-sm opacity-70">{k}</span>
                    </div>
                    <div className="col-span-8">
                      <Input
                        data-testid={`kit-label-${k.toLowerCase()}`}
                        value={kitLabels[k] || ""}
                        onChange={e=>setKitLabels({...kitLabels, [k]: e.target.value})}
                        placeholder={`e.g. ${k === "White" ? "Bailey" : k}`}
                      />
                    </div>
                  </div>
                );
              })}
              <div className="pt-2">
                <button onClick={saveKitLabels} data-testid="kit-labels-save" className="btn-volt px-5 py-2.5 rounded-md">Save labels</button>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="utilization" className="mt-6">
          <UtilisationTab stats={stats} />
</TabsContent>

        <TabsContent value="bookings" className="mt-6">
          <BookingsTab bookings={bookings} onChanged={loadAll} />
</TabsContent>

        <TabsContent value="roles" className="mt-6">
          <RolesTab />
        </TabsContent>

        <TabsContent value="public-api" className="mt-6">
          <PublicApiTab />
        </TabsContent>

        <TabsContent value="waiting-list-emails" className="mt-6">
          <WaitingListEmailsTab />
        </TabsContent>

        <TabsContent value="approval-routing" className="mt-6">
          <ApprovalRoutingTab />
        </TabsContent>

        <TabsContent value="users" className="mt-6">
          <UsersTab users={users} setUsers={setUsers} user={user} impersonate={impersonate} nav={nav} loadAll={loadAll} openNewUser={openNewUser} setOpenNewUser={setOpenNewUser} newUserForm={newUserForm} setNewUserForm={setNewUserForm} newUserResult={newUserResult} setNewUserResult={setNewUserResult} submitNewUser={submitNewUser} setReassignSrc={setReassignSrc} />
</TabsContent>

        <TabsContent value="audit" className="mt-6 space-y-4">
          <AuditTab audit={audit} auditBusy={auditBusy} loadAudit={loadAudit} auditIsRestorable={auditIsRestorable} restoreAudit={restoreAudit} restoringAuditId={restoringAuditId} />
</TabsContent>

        <TabsContent value="emails" className="mt-6 space-y-4">
          <EmailsTab emails={emails} emailsBusy={emailsBusy} loadEmails={loadEmails} />
</TabsContent>

        <TabsContent value="secretary" className="mt-6 space-y-4">
          <SecretaryTab />
</TabsContent>
        <TabsContent value="proposals" className="mt-6 space-y-4">
          <ProposalsTab />
        </TabsContent>
      </Tabs>
      <Dialog open={!!reassignSrc} onOpenChange={(o) => { if (!o) { setReassignSrc(null); setReassignTo(""); } }}>
        <DialogContent className="max-w-md" data-testid="reassign-teams-dialog">
          <DialogHeader><DialogTitle className="font-display tracking-tight">Reassign {reassignSrc?.name}'s teams</DialogTitle></DialogHeader>
          <p className="text-sm opacity-70">Every team currently managed by <b>{reassignSrc?.name}</b> moves to the person you pick (or becomes unassigned). Their confirmed bookings transfer too.</p>
          <UserPicker users={users.filter(u => u.id !== reassignSrc?.id)} value={reassignTo || null} onChange={(v) => setReassignTo(v || "")} noneLabel="— leave unassigned —" placeholder="Pick the new manager" testId="reassign-teams-target" />
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => setReassignSrc(null)} className="text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-border" data-testid="reassign-teams-cancel">Cancel</button>
            <button type="button" disabled={reassignBusy} data-testid="reassign-teams-confirm"
              onClick={async () => {
                setReassignBusy(true);
                try {
                  const { data } = await api.post(`/admin/users/${reassignSrc.id}/reassign-teams`, { to_user_id: reassignTo || null });
                  toast.success(`${data.moved} team${data.moved === 1 ? "" : "s"} reassigned`);
                  setReassignSrc(null); setReassignTo(""); loadAll();
                } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Reassign failed"); }
                finally { setReassignBusy(false); }
              }}
              className="btn-volt text-[10px] tracking-widest uppercase px-4 py-2 rounded disabled:opacity-40">{reassignBusy ? "Moving…" : "Reassign"}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
